/**
 * builderStore — the single Zustand + Immer source of truth for the page being edited (build-plan §5).
 *
 * The content is NORMALIZED (`order[]` + `sectionsById`) so a settings edit touches one map entry and
 * Immer shares every other section reference — letting `MemoSection`s bail out by reference equality for
 * free (build-plan §5.1, §9.2). The canvas, rail, and inspector are sibling subtrees that read this
 * store via selector-scoped subscriptions.
 *
 * M3.3 adds the full FROZEN reducer action set (insert/move/remove/toggle for sections AND blocks),
 * snapshot undo/redo with coalescing, and wires the M3.2 settings reducers through the shared `commit()`
 * helper so EVERY mutation is history-tracked + dirty-bookkept impossible-to-forget (build-plan §5.2).
 * `serialize()` reconstructs the `PageDocument` from `order`+`sectionsById` for the save path (M3.4).
 */
import { loadSectionComponents, sectionRegistry } from '../canvas/sectionRegistry';
import { nanoid } from 'nanoid';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  buildDefaultDocument,
  buildDefaultFooterDocument,
  buildDefaultHeaderDocument,
  buildElectronicsFooterDocument,
  buildElectronicsHeaderDocument,
  buildElectronicsHomeDocument,
  buildElectronicsProductDocument,
  composeChromeSections,
  ELECTRONICS_DEMO_PRODUCT,
  ELECTRONICS_THEME_SETTINGS,
  DEFAULT_THEME_SETTINGS,
  loadThemeDefinition,
  loadThemePayload,
  peekThemePayload,
  hydrateDocument,
  isChromeTemplate,
  wearsStoreChrome,
  splitChromeSections,
  stripChromeSections,
  instantiateBlock,
  instantiateSection,
  migrateThemeSettings,
  applyTextSlots,
  type Block,
  type MenuEntry,
  type PageDocument,
  type PageMeta,
  type ProductActiveOffer,
  type ProductReviews,
  parseCountdownBadges,
  parseProductCountdown,
  type BuyBoxJourney,
  type RenderContext,
  type SectionDefinition,
  type SectionInstance,
  type SettingsValues,
  type StorefrontCollection,
  type StorefrontProduct,
  type StoreLocale,
  type TextPatch,
  type TemplateKey,
  type ThemeSettings,
  localizeThemeDocument,
  parseProductReviews,
} from '@aquilixshop/ui-react';
import { type CodConfig, DEFAULT_COD_CONFIG } from '@aquilixshop/cod-config';
import {
  builderApi,
  type BuilderRoute,
  type CustomPageSummary,
  DEFAULT_ROUTE,
} from '../lib/builderApi';
import {
  activeOfferForProduct,
  catalogApi,
  productResponseToStorefront,
  type ProductListItem,
  type PreviewReviewsAnswer,
  type PreviewReviewsFailure,
} from '../lib/catalogApi';
import { storeApi } from '../lib/storeApi';
import {
  getCollectionStorefrontProducts,
  getStorefrontCollection,
  listStoreCollections,
} from '../lib/collectionsApi';
import {
  adoptEditedChrome,
  adoptHydratedChrome,
  chromeHandleFor,
  chromeHasPendingEdit,
  chromeMatches,
  chromeSections,
  ensureChromeLoaded,
  resetGlobalChrome,
  seedOfflineChrome,
} from './globalChrome';
import { getStoreId } from '../boot/session';
import { isReadOnlyView } from '../boot/view-handoff';
import { HttpError, NoStoreError, ReadOnlyViewError, SessionExpiredError } from '../lib/errors';
import { ensureStoreDictionary } from '@aquilixshop/ui-react/bundled';
import { buildEditContext } from '../canvas/renderContext';
import { applySnapshot, pushHistory, snapshot, type HistoryHost, type Snapshot } from './history';
import { clampToZone, orderWithChromePinned } from './sectionZones';
import { pushToast } from '../ui/toastStore';
import { bt, btp } from '../i18n';
import { flushSave, scheduleAutosave } from './autosave';
import { cancelThemeSave, flushThemeSave, scheduleThemeSave } from './saveTheme';
import {
  dropStagedTheme,
  hydrateStagedTheme,
  isStagedThemeHydrated,
  readStagedTheme,
  stageThemeSettings,
} from './saveThemeWorkspace';

/**
 * A partial theme patch the editor sends to {@link BuilderState.updateThemeSettings}. Shallow-per-group:
 * `colors`/`shape` are flat; `typography` has `heading`/`body` sub-groups. Only the touched leaves need be
 * present. (feature 0019 Slice 2)
 */
export type ThemeSettingsPatch = {
  presetId?: string;
  schemaVersion?: number;
  colors?: Partial<ThemeSettings['colors']>;
  typography?: {
    heading?: Partial<ThemeSettings['typography']['heading']>;
    body?: Partial<ThemeSettings['typography']['body']>;
  };
  shape?: Partial<ThemeSettings['shape']>;
  brand?: Partial<NonNullable<ThemeSettings['brand']>>;
  productBadges?: Partial<NonNullable<ThemeSettings['productBadges']>>;
};

/** Dedicated theme-save status (the theme channel is versionless + decoupled from the page `saveState`). */
export type ThemeSaveState = 'idle' | 'saving' | 'saved' | 'error' | 'offline';

export type SaveState =
  | 'idle'
  | 'unsaved'
  | 'dirty'
  | 'saving'
  | 'saved'
  | 'error'
  | 'conflict'
  | 'offline';
export type LoadState = 'idle' | 'loading' | 'ready' | 'error' | 'no-store' | 'session-expired';

/**
 * WHY the last save failed — the only thing "Retry" can say that is true.
 *
 * `saveState: 'error'` collapses everything into one red "Couldn't save": a routing refusal that never
 * reached the network, an expired session, a store that moved under the tab, a 500. Retry then re-ran the
 * SAME doomed attempt in silence — same label, same screen, nothing in the console — which reads as a dead
 * button (founder, 2026-09-18). The kind rides with the state so the retry can name the cause and, when a
 * retry cannot possibly land, say what would.
 *
 *  - `route-mismatch` — the document belongs to another page than the route (the identity guard in
 *    autosave). NOTHING is sent; retrying is refused again, forever, until the editor is back on that page.
 *  - `store-changed` / `no-store` / `session` — the write can't land as this tab stands; the remedy is a
 *    reopen / a sign-in, not another PUT.
 *  - `network` / `server` — genuinely worth another try.
 */
export type SaveFailureKind =
  | 'route-mismatch'
  | 'no-store'
  | 'session'
  | 'store-changed'
  | 'network'
  | 'server';

export type SaveFailure = {
  kind: SaveFailureKind;
  /** HTTP status when the server answered at all. */
  status?: number;
  /** Human detail the toast can name — e.g. the page the document actually belongs to. */
  detail?: string;
  /** Which trigger failed: a failed PUBLISH must be retried as a publish, not as a draft save. */
  reason?: 'auto' | 'manual' | 'publish' | 'route-leave';
};

/** Only these two can succeed on a second attempt; the others need the seller to do something first. */
export function isRetryableSaveFailure(f: SaveFailure | null | undefined): boolean {
  return f == null || f.kind === 'network' || f.kind === 'server';
}

/** Device-preview presets (build-plan §4.4). CSS width-framing only — the SAME React tree stays mounted. */
export type DevicePreview = 'desktop' | 'tablet' | 'mobile';

/** Catalog-list fetch status for the merged page selector's product picker (feature 0021). */
export type CatalogState = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * One named template as the selector lists it: its handle plus whether it is PUBLISHED. Only a PUBLISHED
 * template can be assigned to a product — the storefront serves published rows, so assigning a draft-only
 * handle would leave the product silently on `default`. The selector badges the difference.
 */
export interface TemplateEntry {
  handle: string;
  status: 'DRAFT' | 'PUBLISHED';
  hasUnpublishedChanges: boolean;
}

/** Status of the one-shot store-identity fetch (the slug that builds the storefront "Preview" URL). */
export type StoreLoadState = 'idle' | 'loading' | 'loaded' | 'error';

const DEVICE_STORAGE_KEY = 'aqx.builder.device';

/** Read the persisted device choice (build-plan §4.4 "persist the choice"); default desktop. */
function readPersistedDevice(): DevicePreview {
  try {
    const v = globalThis.localStorage?.getItem(DEVICE_STORAGE_KEY);
    if (v === 'desktop' || v === 'tablet' || v === 'mobile') return v;
  } catch {
    // localStorage can throw (private mode / disabled) — fall back to the default, never crash boot.
  }
  return 'desktop';
}

function persistDevice(device: DevicePreview): void {
  try {
    globalThis.localStorage?.setItem(DEVICE_STORAGE_KEY, device);
  } catch {
    // Best-effort persistence; an in-memory choice still works for the session.
  }
}

/** Document fields kept verbatim and re-emitted on save (everything but `sections`). */
type DocMeta = Pick<PageDocument, 'schemaVersion' | 'template' | 'handle' | 'locale'> & {
  meta?: PageMeta;
};

export interface BuilderState extends HistoryHost {
  // ── content (normalized; serialized to PageDocument.sections only at save) ──
  order: string[];
  sectionsById: Record<string, SectionInstance>;
  meta: DocMeta;
  route: BuilderRoute;
  /** Built once on load, kept stable (build-plan §9.3) — never reconstructed inline in render. */
  renderContext: RenderContext | null;
  /** Feature 0018 M2 — the REAL product bound into the product-template preview (null = demo fallback). */
  previewProduct: StorefrontProduct | null;
  /**
   * Feature 0041 V6 — the Countdown APP's own surfaces for the page being edited, as the storefront would
   * serve them. The BANNER and the image CHIPS ride the render context (the sections read them there, exactly
   * as on a live page); the BAR and the POPUP have no section to live in — the storefront's LAYOUT mounts
   * them — so the canvas keeps them here and paints them around the document.
   *
   * Raw wire payloads, parsed at the paint site by the same `ui-react` parsers the storefront uses. `null`
   * = nothing to paint, which is also what an app that is not installed answers.
   */
  countdownBar: unknown;
  countdownPopup: unknown;
  /**
   * The Countdown APP's surface the seller has selected on the canvas (feature 0041 V7) — the editor's
   * third kind of selection, beside a section and a block.
   *
   * « The builder is just for the design like font size font weight colors padding » (fondatrice,
   * 29/09/2026). These surfaces are not in the page document, so they have no section id to select; this
   * is what the inspector keys its design panel on. Mutually exclusive with a section/block selection.
   */
  selectedCountdown: { campaignId: string; surface: 'bar' | 'popup' } | null;
  /**
   * Feature 0026-07 — the bound preview product's active per-product quantity offer, in the render shape
   * (fetched store-side from GET /api/quantity-offers and mapped via {@link activeOfferForProduct}). Threaded
   * into `renderContext.activeOffer` so the buy-box `quantity` block previews the seller's offer TILES instead
   * of the classic stepper. `null` when the bound product has no offer / the fetch failed / on the demo
   * (fail-open). Fetched asynchronously whenever `previewProduct` is bound to a real product.
   */
  previewOffer: ProductActiveOffer | null;
  /**
   * Feature 0066 — the bound preview product's PUBLISHED reviews, in the storefront's own shape
   * (GET /api/store/apps/reviews/storefront-preview), merged into the render context by the canvas.
   *
   * It is NOT a parameter of {@link buildEditContext} on purpose. That context is rebuilt from a dozen
   * places — a theme change, a locale flip, a route change, a restored cache entry — and a value threaded
   * through all of them is a value that silently goes missing from one of them. Merged at render time
   * instead, the reviews survive every rebuild, and there is exactly one line that decides they are there.
   *
   * `null` on the demo product, on a product with no published review, or on any failure: the section then
   * draws its own editing placeholder, which is the honest thing to show.
   */
  previewReviews: ProductReviews | null;
  /**
   * The reviews of the BOUND product have been asked for and the answer is not back (feature 0066).
   *
   * Derived, never guessed: true exactly while the bound product's id is absent from
   * `previewReviewsCache`. `previewReviews: null` cannot carry this on its own — it means BOTH "this
   * product has no published review" and "we have not asked yet", and the canvas printed "0 reviews" for
   * the second, once per navigation, for a whole round trip (founder, 16/09/2026).
   */
  previewReviewsLoading: boolean;
  /**
   * WHY the bound product's reviews are missing, when the reason is a FAILURE and not the shop (0066).
   *
   * The third state the canvas never had. `previewReviews: null` + `previewReviewsLoading: false` used to
   * mean "this product has no published review" AND "the call blew up", and the section printed the first
   * sentence for both — over a shop with four hundred, with no spinner, no message and no retry, until the
   * seller reloaded the editor (founder, 18/09/2026: the local backend had been down since 20h40 and the
   * band simply read «0 avis»). `null` here = no failure to report.
   */
  previewReviewsError: PreviewReviewsFailure | null;

  // ── catalog cache (feature 0021) — the seller's product list, fetched ONCE and shared by the merged
  // page selector so the "Change product" list opens from cache (no per-open spinner on the hot path). ──
  /** The cached product list (empty until first `loadCatalog()`). */
  catalog: ProductListItem[];
  /** Catalog fetch status (`loaded` even for an empty catalog; `error` = last fetch failed). */
  catalogState: CatalogState;

  // ── template reach — the two facts that make the product/template relationship legible in the editor.
  // Neither is derivable from the catalog cache (capped at 100 products), so both come from the server. ──
  /**
   * How many products render each product-page template, keyed by handle (unassigned products counted under
   * `default`). `null` until `loadTemplateUsage()` resolves — the not-yet-loaded state has to be a property
   * of the MAP, not of a key, because the server omits zero-count handles entirely: once loaded, a missing
   * key means zero (a template nothing renders), which is precisely the case worth warning about.
   */
  templateUsage: Record<string, number> | null;
  /**
   * The template the PREVIEWED product actually renders (`null` = the base `default` page, and also the
   * value while the built-in demo product is bound). This is the fact the selector needs to say "you are
   * editing a layout this product is not assigned to" — previewing is not assigning.
   */
  previewProductTemplate: string | null;
  /**
   * Is {@link BuilderState.previewProductTemplate} an ANSWER, or merely its empty default? The two were
   * indistinguishable until 26/10/2026, and the warning that matters most paid for it: a product created a
   * minute ago carries no suffix, so it renders `default` and its template reads `null` — the very same
   * `null` a cold boot holds before the restored product has been revalidated. The "Not on this product"
   * pill, silent on `null` to avoid crying wolf on a cold boot, was therefore silent for EVERY brand-new
   * product — the founder created one, created a layout for it, and had nothing on screen telling her the
   * two were unrelated. `true` only after a server answer (bind / revalidate / assign) names the template.
   */
  previewProductTemplateKnown: boolean;

  // ── preview collection (22/09/2026) — the REAL collection bound into the collection-template preview.
  // « Cette page doit fetcher la data de la collection où on est et pas me montrer une liste statique » :
  // until this the collection template painted the built-in demo collection ("Nouveautés", 24 sample
  // products) whatever the store sold, so the editor and the storefront never showed the same page. ──
  /** The bound collection (`null` = the built-in demo collection). */
  previewCollection: StorefrontCollection | null;
  /** Binding status — `loading` while the collection + its products are fetched, `error` when that failed. */
  previewCollectionState: CatalogState;
  /** The store's ACTIVE collections, fetched once for the "Change collection" list. */
  collectionsCatalog: readonly StorefrontCollection[];
  collectionsCatalogState: CatalogState;

  // ── store identity — the slug that builds the storefront "Preview" URL (the JWT only carries the
  // opaque store_id; the public storefront resolves stores by slug). Fetched once, best-effort. ──
  /** The seller's store slug (null until `loadStore()` succeeds). */
  storeSlug: string | null;
  /**
   * The shop's PUBLIC address when it is a custom domain (feature 0046): the primary VERIFIED domain, null
   * when the platform subdomain is the address (or until `loadStore()` succeeds). "Preview" and "View store"
   * open it on a deployed builder — the same door as the dashboard's eye, not the slug host rebuilt by hand.
   */
  storePrimaryDomain: string | null;
  /**
   * The store's DISPLAY NAME, from `loadStore()`. The slug is an address, not a brand: every AI brief
   * that opened on «Boutique : storetest» was telling the model the store is called storetest.
   * Empty until loaded — the callers fall back to the slug.
   */
  storeName: string;
  /** The store's selling country (ISO-3166 a2) + currency (ISO-4217), from `loadStore()`. Drive the
   *  cod-form preview's city source + phone example. Empty country until loaded (→ free-text city). */
  storeCountry: string;
  storeCurrency: string;
  /**
   * The store's own language, from `loadStore()` (feature 0050 lot 0). The canvas used to take its
   * locale from the loaded DOCUMENT, whose `locale` field can disagree with the row it was read from —
   * koffti's rows were moved to `ar` while their documents still said `fr`, so the canvas rendered
   * left-to-right a store the storefront renders right-to-left. Empty until loaded.
   */
  storeLanguage: string;
  /**
   * Does the store actually accept CARD payments (a connected, card-active Stripe account)? From
   * `loadStore()` (GET /api/store/payments/account). `null` = not known yet / the fetch failed — the
   * builder then stays neutral (nothing greyed, the canvas previews the picked method). Once known it
   * drives BOTH authoring surfaces: the buy-box "Carte bancaire (Stripe)" option is greyed in the
   * inspector, and `renderContext.enabledPaymentMethods` makes the canvas preview what the customer
   * really gets — the COD funnel — instead of a card checkout the store can't take.
   */
  storePaymentMethod: 'CARD' | 'COD' | null;
  /**
   * The buy-boxes of every OTHER draft page of the store, with the journey each stores
   * (`GET /api/builder/buy-box-journeys`), loaded with each page open; the open page's own are read live.
   * Behind the header's "Show cart" switch, which is a STORE-wide question (see `state/cartMode.ts`).
   * `null` = not known (not loaded yet, or the fetch failed) → the switch stays locked, never one that flips.
   */
  buyBoxJourneysElsewhere: readonly BuyBoxJourney[] | null;
  /** Store-identity fetch status (`error` = the Preview button stays disabled). */
  storeState: StoreLoadState;

  // ── global theme (feature 0019) — its OWN slice + versionless save channel (NOT the page draft) ──
  /** The store's global theme; drives `renderContext.themeSettings` → the whole canvas re-themes live. */
  themeSettings: ThemeSettings;
  /**
   * The store's COD app config (feature 0026), fetched once with the theme and kept live. Drives
   * `renderContext.codConfig` so the buy-box `cod-form` block previews the SAME form the seller designed in
   * the dashboard COD app. Read-only in the builder (authored in the dashboard, not here) — so it is NOT on
   * the undo timeline, unlike the theme.
   */
  codConfig: CodConfig;
  /** Dedicated theme-save status (decoupled from the page `saveState`; owned by `saveTheme.ts`). */
  themeSaveState: ThemeSaveState;
  /** Wall-clock of the last successful theme PUT (null = never). */
  themeSavedAt: number | null;
  /**
   * The editor is inside a THEME WORKSPACE and its ThemeSettings await publishTheme (spec 0032).
   * While true, saveTheme's channel is SUPPRESSED (0019 Q1 is live-on-save — an early PUT would
   * re-skin the LIVE store before publish). Set/cleared by `syncThemeWorkspace` per route.
   */
  themeStagedForPublish: boolean;
  /**
   * Snapshot of the LIVE 0019 settings row (first-load fetch, updated on every successful live PUT).
   * Restored when the seller leaves a theme workspace — `load()` never re-fetches the theme.
   */
  liveThemeSettings: ThemeSettings;

  // ── optimistic-lock + publish (populated by load; swapped by M3.4 autosave) ──
  serverVersion: number | null;
  publishedAt: string | null;
  hasUnpublishedChanges: boolean;

  // ── selection (snapshotted with history) ──
  selectedSectionId: string | null;
  selectedBlockId: string | null;
  /**
   * PART-LEVEL selection (field hotspots): the schema `field.id` of the setting the seller drilled into
   * (a section/block heading, CTA, image, …). Drives the canvas frame on that exact element AND scrolls
   * the inspector straight to that field. UI-only — like `selectMode`, it is NOT snapshotted, NOT
   * persisted, NOT undoable; it's cleared whenever the selected node changes.
   */
  focusedFieldId: string | null;

  // ── history (M3.3) — typed snapshot stacks + coalescing markers (from HistoryHost) ──
  past: Snapshot[];
  future: Snapshot[];
  _lastMergeKey: string | null;
  _lastEditAt: number;

  // ── sync status ──
  dirty: boolean;
  saveState: SaveState;
  /** Why the last save failed, when it did — see {@link SaveFailure}. Cleared by the next success. */
  saveFailure: SaveFailure | null;
  lastSavedAt: number | null;
  loadState: LoadState;

  // ── UI-only view state (M3.5) — NOT history-tracked, NOT part of the saved document ──
  /** Device-preview frame width (build-plan §4.4); CSS-only, same tree stays mounted. Persisted. */
  device: DevicePreview;
  /** Clean preview mode: render the canvas without the EditableSection chrome (build-plan §4.4). */
  previewMode: boolean;
  /**
   * Shopify-style "select mode": while ON, a click ANYWHERE on the canvas selects the child BLOCK under the
   * pointer (opens its config in the inspector + highlights it in the left rail), instead of selecting the
   * whole section. UI-only — never persisted, never undoable, never dirtying.
   */
  selectMode: boolean;
  /**
   * Monotonic "scroll the current selection into view" request counter (Shopify parity). Bumped by the
   * left tree (and section insert/duplicate) — NOT by canvas clicks — so choosing a component in the rail
   * jumps the preview to it even from far away (e.g. the footer), while clicking a section you can already
   * see never repositions the canvas. UI-only: never snapshotted, persisted, or undoable. The canvas keys
   * a scroll effect on this so re-picking the SAME node re-fires (see `useRevealSelection`).
   */
  revealNonce: number;

  // ── actions: load + selection ──
  load: (route?: BuilderRoute) => Promise<void>;
  /** Warm the in-session page cache for a route (feature 0021) so a later switch to it is instant. Best-
   *  effort, off the critical path — no-op if already cached/in-flight or before the first load. */
  prefetchRoute: (route: BuilderRoute) => Promise<void>;
  /**
   * APPLY-THEME boot flow (theme registry, spec 0032): look the theme up in the registry, seed a DRAFT
   * for every template it ships (via the SAME getDraft→saveDraft path the editor uses), stage its
   * ThemeSettings through the existing 0019 theme channel, then open the `index` template as usual.
   * NEVER calls publish — the live store only changes when the seller clicks the existing Publish.
   * Unknown / coming-soon / payload-less ids and seeding failures fall back to a normal `load(route)`.
   */
  applyTheme: (
    themeId: string,
    route?: BuilderRoute,
    opts?: { readonly reset?: boolean },
  ) => Promise<void>;
  /**
   * Route ↔ staging sync, called after EVERY load: entering a theme workspace stages that theme's
   * persisted (or registry-default) settings locally AND — when the theme ships a `demoProduct` and
   * no preview product is bound — binds that demo EPHEMERALLY (never persisted) so the workspace's
   * product page previews on-brand content; leaving one restores the live theme, drops the
   * suppressed pending save, and unbinds a still-bound theme demo. Idempotent.
   */
  syncThemeWorkspace: () => void;
  /**
   * Publish the CURRENT theme workspace (spec 0032): copy each workspace draft over the live handle
   * ('default'), publish it, then release+flush the staged ThemeSettings — the one moment the live
   * store changes. No-op (false) outside a workspace. The workspace rows remain as this theme's draft.
   */
  publishTheme: () => Promise<boolean>;
  selectSection: (sectionId: string | null) => void;
  selectBlock: (blockId: string | null) => void;
  /** Drill into (or clear) a specific setting of the selected node — frames the element + jumps the inspector. */
  focusField: (fieldId: string | null) => void;

  // ── named-template manager (feature 0018) ──
  /** The PUBLISHED handle suffixes for the CURRENT (template, locale) — for the Topbar selector. */
  listTemplateHandles: () => Promise<string[]>;
  /**
   * The CURRENT (template, locale)'s templates WITH their publish state — the selector lists these so a
   * draft-only template (which no product can be assigned to yet) is visible and badged rather than absent.
   *
   * A failure yields `null`, NEVER `[]` — the same rule as {@link BuilderState.templateUsage}. `[]` means
   * "this store has no named template", and the selector renders that as a short, confident list (the base
   * `default` plus the open handle). A failed request rendered that way told the founder her other layouts
   * had vanished. Silence about a failure is the only honest answer; the caller shows a retry.
   */
  listTemplateEntries: () => Promise<TemplateEntry[] | null>;
  /** Refresh {@link BuilderState.templateUsage} — how many products render each template, store-wide. */
  loadTemplateUsage: () => Promise<void>;
  /**
   * Assign the PREVIEWED product to `handle` (null → back to the base `default` page), then refresh the
   * usage counts. Throws when the write fails (e.g. 422 on a not-yet-published template) — the caller
   * surfaces it. Requires a real preview product; the demo product cannot be assigned.
   */
  assignTemplateToPreviewProduct: (handle: string | null) => Promise<void>;
  /**
   * Assign a NAMED product (by id) to `handle`, then refresh the usage counts — the same write as
   * {@link BuilderState.assignTemplateToPreviewProduct} without the "whichever product happens to sit in
   * the preview" implicitness (founder, 20/09/2026: "we should specify the product we are applying the
   * template to"). The publish flow uses it to apply a freshly-published layout to a product she picked
   * by name, which is not necessarily the one bound to the canvas.
   *
   * Keeps `previewProductTemplate` honest when the target IS the previewed product, so the selector's
   * "Uses X" line never contradicts a write that just landed.
   */
  assignTemplateToProduct: (productId: string, handle: string | null) => Promise<void>;
  /** Switch the edited document to an existing named template (handle) of the current type. */
  selectTemplate: (handle: string) => Promise<void>;
  /**
   * Create a named template under `handle`, as a COPY of `source` (another handle of the same type;
   * `default` is the base page). A template is never born blank — a seller starts from a page she has
   * already seen, not from the theme's raw seed (founder, 19/09/2026). Copying the handle currently open
   * takes the document as it is ON SCREEN (unsaved edits included); any other source is read from its
   * saved draft. Ids are always refreshed, so the copy is its own page. Persisted + published immediately.
   */
  createTemplate: (handle: string, source?: string) => Promise<void>;

  // ── custom CMS pages manager (feature 0027 M1) ──
  /** The store's custom pages (About/FAQ/CGV…) for the current locale — for the Topbar "Pages" submenu. */
  listPages: () => Promise<CustomPageSummary[]>;
  /**
   * Create a NEW custom page from a `title`: the server slugifies it to a unique handle, persists the
   * starter document, and the builder opens that handle. Resolves to the new handle.
   *
   * `source` (optional) = start ON TOP of an existing page instead of the blank starter: that route's
   * CURRENT content becomes the new page's, so the seller never has to rebuild a layout from scratch.
   * Omitted → the blank one-rich-text starter (unchanged behaviour).
   */
  createPage: (title: string, source?: NewPageSource) => Promise<string>;
  /** Rename a custom page's title (the handle stays immutable). */
  renamePage: (handle: string, title: string) => Promise<void>;
  /** Delete a custom page; if it is the one being edited, fall back to the home page. */
  deletePage: (handle: string) => Promise<void>;
  /** Bind a REAL product into the product-template preview (rebuilds the render context). */
  setPreviewProduct: (product: StorefrontProduct | null) => void;
  /**
   * Merge product-picker refs the canvas resolved (feature 0023) into `renderContext.resolved.products`, so a
   * section bound to a picked product paints THAT product in the editor instead of the sample fallback (see
   * `usePickedProducts`). A no-op when every entry is already present by reference — the caller re-applies its
   * whole cache after any context rebuild (theme undo, page switch, preview-product binding drops `resolved`),
   * and that self-healing loop must terminate.
   */
  mergeResolvedProducts: (products: Record<string, StorefrontProduct>) => void;
  /**
   * Remove product ids from `renderContext.resolved.products` — the counterpart of
   * {@link mergeResolvedProducts}. Used when the server stops serving a picked product (404, or it left
   * ACTIVE): the canvas must not keep painting a card the shop will not show, so the section falls back to
   * its sample instead. Same identity guard: a no-op when none of the ids is resolved.
   */
  dropResolvedProducts: (ids: readonly string[]) => void;
  /**
   * Merge the navigation menus the canvas resolved into `renderContext.resolved.menus`, so a header/footer
   * with a PICKED menu renders that menu's real items in the editor (the storefront gets them from the page
   * bundle; the builder fetches them — see `useResolvedMenus`). Same identity guard as
   * {@link mergeResolvedProducts}: a no-op when nothing changed, so the caller's re-apply-after-rebuild
   * effect terminates.
   */
  /**
   * Publish what the Countdown app would paint on this page (feature 0041 V6).
   *
   * The founder asked the right question — « le builder doit la lire directement de l'appli » — and this is
   * where the answer lands: the banner and the chips go onto the render context so the very sections that
   * paint them on the storefront paint them here, and the bar/popup are kept beside it for the canvas
   * chrome. Same by-reference guard as the other merges, so the re-apply-after-rebuild effect terminates.
   */
  /** Select (or clear) the Countdown app's surface; clears any section/block selection. */
  selectCountdownSurface: (target: { campaignId: string; surface: 'bar' | 'popup' } | null) => void;
  /**
   * Apply a design change to the selected campaign's surfaces, in the canvas, NOW (0041 V7).
   *
   * Optimistic on purpose: the save is a round-trip to the backend, and a colour picker that repaints a
   * quarter-second after the drag is a colour picker nobody can aim with. The payloads the canvas paints
   * from are patched here; the network write is the panel's business.
   */
  patchCountdownDesign: (campaignId: string, design: Record<string, unknown>) => void;
  applyCountdownPreview: (preview: {
    banner: unknown;
    bar: unknown;
    popup: unknown;
    badges: Readonly<Record<string, unknown>>;
  }) => void;
  mergeResolvedMenus: (menus: Record<string, readonly MenuEntry[]>) => void;
  /**
   * Merge collection-picker refs the canvas resolved into `renderContext.resolved.collections`, so a
   * `collection-list` in "Selected collections" mode (and any other section bound to a collection) paints
   * the REAL collection in the editor instead of the demo fallback. Same identity guard as
   * {@link mergeResolvedProducts} so the caller's re-apply-after-rebuild effect terminates.
   */
  mergeResolvedCollections: (collections: Record<string, StorefrontCollection>) => void;
  /**
   * Merge a featured collection's MEMBER PRODUCTS into `renderContext.resolved.collectionProducts`, so
   * `featured-collection` paints the collection's real product cards in the editor instead of an empty grid.
   * The storefront gets them in the page bundle; the builder fetches them per picked collection (see
   * `useResolvedCollections`). An EMPTY array is a real answer and must be stored — the section reads
   * "absent" as "nobody answered yet" (demo) and "empty" as "this collection sells nothing" (render nothing).
   * Same identity guard as {@link mergeResolvedProducts} so the re-apply-after-rebuild loop terminates.
   */
  mergeResolvedCollectionProducts: (
    productsByCollection: Record<string, readonly StorefrontProduct[]>,
  ) => void;
  /**
   * Publish the store's ACTIVE collections into `renderContext.resolved.storeCollections`, the list a
   * `collection-list` in "All my collections" mode renders. The storefront gets it from the page bundle;
   * the builder fetches it (see `useResolvedCollections`). A no-op when the list is already the same
   * reference, so the re-apply-after-rebuild loop settles after one pass.
   */
  setStoreCollections: (collections: readonly StorefrontCollection[]) => void;
  /**
   * Fetch the seller's product list ONCE into the shared `catalog` cache (feature 0021). Idempotent: a
   * second call while `loaded`/`loading` is a no-op, so it's safe to prefetch on idle AND on menu-open.
   * Best-effort — a failure sets `catalogState:'error'` and never throws.
   */
  loadCatalog: () => Promise<void>;
  /**
   * Fetch the seller's store slug ONCE so the topbar "Preview" button can open the public storefront.
   * Idempotent (no-op while `loaded`/`loading`) and best-effort — a failure sets `storeState:'error'`
   * (the button stays disabled) and never throws.
   */
  loadStore: () => Promise<void>;
  /**
   * Bind the product-template preview by catalog id (feature 0021): fetch the full product and swap it in
   * (`null` → the built-in demo). Fetches the full product because the render context needs the complete
   * StorefrontProduct, not the lean list row. Rejects on a fetch failure so the caller can surface it.
   */
  bindPreviewProduct: (id: string | null) => Promise<void>;
  /**
   * Fetch the store's ACTIVE collections ONCE into `collectionsCatalog` (idempotent, best-effort) — the list
   * the page selector's "Change collection" submenu draws from.
   */
  loadCollectionsCatalog: () => Promise<void>;
  /**
   * Bind a REAL collection (+ its storefront products) into the collection-template preview, or `null` for
   * the demo. Persisted per store so a reload keeps it. The canvas re-renders with `routeType: 'collection'`,
   * exactly the page shape the live storefront passes — the banner shows the collection's name and the grid
   * its products, not "Nouveautés".
   */
  bindPreviewCollection: (
    id: string | null,
    opts?: { preloaded?: StorefrontCollection; repaint?: boolean },
  ) => Promise<void>;
  /**
   * On a collection route with nothing bound: restore the remembered collection, else bind the store's FIRST
   * active collection. A seller opening the template sees her shop from the first paint, never the demo.
   */
  ensurePreviewCollection: (forRoute?: BuilderRoute, opts?: { repaint?: boolean }) => Promise<void>;

  /**
   * Edit the global theme (feature 0019). Deep-merges the patch, pushes an undo snapshot, rebuilds the
   * render context so the canvas re-themes live, and schedules the DEDICATED theme save (NOT the page
   * autosave). NEVER dirties the page or bumps its `@Version`.
   */
  updateThemeSettings: (patch: ThemeSettingsPatch) => void;

  // ── UI-only view actions (M3.5) ──
  setDevice: (device: DevicePreview) => void;
  setPreviewMode: (on: boolean) => void;
  togglePreviewMode: () => void;
  setSelectMode: (on: boolean) => void;
  toggleSelectMode: () => void;
  /**
   * Set / flip the PREVIEW (canvas content) reading direction — this is the seller-facing "show my store
   * in RTL" control. It flips `renderContext.dir` (so both the `[data-aqx-page]` wrapper AND every rendered
   * section reads the new dir), NOT the builder chrome, which always stays LTR. Seeded from the store
   * locale on load (Arabic → RTL); the toggle overrides it for the session. UI-only: never dirties/undoes.
   */
  setPreviewDir: (dir: 'ltr' | 'rtl') => void;
  togglePreviewDir: () => void;
  /** Ask the canvas to scroll the CURRENT selection (block › section) into view (rail/tree picks). */
  requestCanvasReveal: () => void;

  // ── FROZEN section reducers (build-plan §5.2 — keyed by id, NEVER array index) ──
  insertSection: (type: string, atIndex?: number, presetIndex?: number) => void;
  moveSection: (id: string, toIndex: number) => void;
  removeSection: (id: string) => void;
  duplicateSection: (id: string) => void;
  toggleSectionDisabled: (id: string) => void;

  // ── FROZEN block reducers (recursive: target a nested container via `parentBlockId`) ──
  addBlock: (
    sectionId: string,
    blockType: string,
    atIndex?: number,
    parentBlockId?: string | null,
  ) => void;
  moveBlock: (sectionId: string, blockId: string, toIndex: number) => void;
  removeBlock: (sectionId: string, blockId: string) => void;
  duplicateBlock: (sectionId: string, blockId: string) => void;
  toggleBlockDisabled: (sectionId: string, blockId: string) => void;

  // ── settings reducers (M3.2) — partial keyed patches, now history-wrapped + coalesced ──
  updateSectionSettings: (sectionId: string, patch: SettingsValues) => void;
  updateBlockSettings: (sectionId: string, blockId: string, patch: SettingsValues) => void;
  /**
   * Write AI-generated text through the SAME edit path the inspector uses (feature 0059 lot 1d):
   * applyTextSlots validates every address/writability/maxLength against the live document, the result
   * lands via commit() — so history holds one undo frame per generated section (the seller can Ctrl-Z a
   * generation like any edit) and autosave persists it. Returns what actually landed; rejects are the
   * caller's to report, and what it must not bill.
   */
  applyGeneratedText: (patches: TextPatch[]) => { applied: number; rejected: number };

  // ── undo/redo (build-plan §5.3) ──
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
}

/**
 * order[] + sectionsById → the FULL editing surface, chrome included. This is what the seller sees and
 * what the canvas renders; it is NOT the save shape (see {@link serialize}) — it is used as the dirty
 * marker, so an edit to the header counts as an edit even though it is persisted elsewhere.
 */
export function serializeComposed(
  state: Pick<BuilderState, 'order' | 'sectionsById' | 'meta'>,
): PageDocument {
  return {
    ...state.meta,
    sections: state.order
      .map((id) => state.sectionsById[id])
      .filter((s): s is SectionInstance => !!s),
  };
}

/**
 * The PAGE document to persist (the save shape, M3.4) — chrome-free.
 *
 * Since 2026-08-16 a page document may not carry the header / announcement bar / ticker / footer: those
 * belong to the store's two GLOBAL chrome documents (see `globalChrome.ts`), which the builder composes
 * into this same flat list on load and saves separately. Stripping here — at the ONE choke-point every
 * writer goes through (autosave, keepalive, theme-workspace seeding, page duplication) — is what makes
 * "a page never owns a header" true by construction rather than by everyone remembering.
 *
 * A document whose OWN template is `header`/`footer` is passed through whole: there, the chrome IS the
 * page.
 */
export function serialize(
  state: Pick<BuilderState, 'order' | 'sectionsById' | 'meta'>,
): PageDocument {
  const doc = serializeComposed(state);
  return isChromeTemplate(doc.template) ? doc : stripChromeSections(doc);
}

/**
 * The chrome half of the editing surface — what `globalChrome` persists into the two global documents.
 *
 * `null` for a document that carries NO chrome (the welcome popup, 0074). That is not a convenience: the
 * editing surface of such a page holds no header and no footer, so splitting it yields two EMPTY lists —
 * and every caller hands those straight to `adoptEditedChrome`, which would make the store's real header
 * and footer vanish from memory and be written out empty on the next save. Opening the popup would erase
 * the shop's header. A `null` the callers must look at is the only shape that cannot be used by accident.
 */
export function serializeChrome(state: Pick<BuilderState, 'order' | 'sectionsById' | 'meta'>): {
  header: SectionInstance[];
  footer: SectionInstance[];
} | null {
  if (!wearsStoreChrome(state.meta.template)) return null;
  const { header, footer } = splitChromeSections(serializeComposed(state).sections);
  return { header, footer };
}

function keyById(sections: SectionInstance[]): Record<string, SectionInstance> {
  const map: Record<string, SectionInstance> = {};
  for (const s of sections) map[s.id] = s;
  return map;
}

/** Re-mint every block's nanoid id, recursively (a duplicated template must not share ids with its source). */
function reidBlocks(blocks: Block[] | undefined): Block[] | undefined {
  if (!blocks) return blocks;
  return blocks.map((b) => ({ ...b, id: nanoid(), blocks: reidBlocks(b.blocks) }));
}

/**
 * Deep-clone a document with FRESH section + block ids (feature 0018 duplicate). Ids are global keys
 * (React/dnd/reducer targets), so a "Duplicate from" must re-stamp them — else `product.default` and
 * `product.advertorial` would collide. Settings/values are shallow-copied (the duplicate is independent
 * thereafter; edits go through the keyed reducers which replace, never mutate, the shared refs).
 */
export function reidDocument(doc: PageDocument): PageDocument {
  return {
    ...doc,
    sections: doc.sections.map((s) => ({ ...s, id: nanoid(), blocks: reidBlocks(s.blocks) ?? [] })),
  };
}

function pickMeta(doc: PageDocument): DocMeta {
  return {
    schemaVersion: doc.schemaVersion,
    template: doc.template,
    handle: doc.handle,
    locale: doc.locale,
    meta: doc.meta,
  };
}

/**
 * Deep-merge a {@link ThemeSettingsPatch} onto the current theme (feature 0019). Per-group shallow merge
 * (`colors`/`shape` flat, `typography.heading`/`body` nested), returning a BRAND-NEW object so the render
 * context + undo snapshots never alias a mutated one. Untouched groups keep their reference.
 */
function mergeThemeSettings(base: ThemeSettings, patch: ThemeSettingsPatch): ThemeSettings {
  return {
    ...base,
    ...(patch.presetId !== undefined ? { presetId: patch.presetId } : {}),
    ...(patch.schemaVersion !== undefined ? { schemaVersion: patch.schemaVersion } : {}),
    colors: patch.colors ? { ...base.colors, ...patch.colors } : base.colors,
    typography: patch.typography
      ? {
          heading: patch.typography.heading
            ? { ...base.typography.heading, ...patch.typography.heading }
            : base.typography.heading,
          body: patch.typography.body
            ? { ...base.typography.body, ...patch.typography.body }
            : base.typography.body,
        }
      : base.typography,
    shape: patch.shape ? { ...base.shape, ...patch.shape } : base.shape,
    // THE FAVICON IS THE STORE'S, NOT THE THEME'S. Every theme-wide replacement goes through here as a
    // patch — applying a theme, entering/leaving a workspace, undo/redo — and none of those objects
    // carries a `brand` key (the theme registry does not define one), so the seller's favicon survives
    // them all instead of being wiped by trying a theme on.
    brand: patch.brand ? { ...base.brand, ...patch.brand } : base.brand,
    // THE URGENCY BADGES ARE THE STORE'S, NOT THE THEME'S — same rule as the favicon just above, and for
    // the same reason: a theme-wide replacement (applying a theme, entering a workspace, undo/redo) never
    // carries a `productBadges` key, so the seller's chip settings survive trying a theme on instead of
    // being silently switched back off.
    productBadges: patch.productBadges
      ? { ...base.productBadges, ...patch.productBadges }
      : base.productBadges,
  };
}

/**
 * The undo-coalescing key for a theme patch: the sorted leaf paths it touches. Consecutive edits to the
 * SAME field(s) within {@link COALESCE_MS} collapse into ONE undo entry (a color/slider drag = one undo),
 * mirroring `updateSectionSettings`. Structural (non-theme) ops never share this key.
 */
function themeMergeKey(patch: ThemeSettingsPatch): string {
  const parts: string[] = [];
  if (patch.colors) parts.push(...Object.keys(patch.colors).map((k) => `colors.${k}`));
  if (patch.typography?.heading)
    parts.push(...Object.keys(patch.typography.heading).map((k) => `typography.heading.${k}`));
  if (patch.typography?.body)
    parts.push(...Object.keys(patch.typography.body).map((k) => `typography.body.${k}`));
  if (patch.shape) parts.push(...Object.keys(patch.shape).map((k) => `shape.${k}`));
  if (patch.brand) parts.push(...Object.keys(patch.brand).map((k) => `brand.${k}`));
  if (patch.productBadges)
    parts.push(...Object.keys(patch.productBadges).map((k) => `productBadges.${k}`));
  return `theme:${parts.sort().join(',')}`;
}

/**
 * True when two states hold the SAME section document (same order + every section by reference). A
 * theme-only undo/redo frame leaves the document untouched (theme edits never mutate sections), so this
 * lets undo/redo persist the theme via its own channel WITHOUT spuriously dirtying the page (feature 0019).
 */
function sameDocument(
  a: { order: string[]; sectionsById: Record<string, SectionInstance> },
  b: { order: string[]; sectionsById: Record<string, SectionInstance> },
): boolean {
  if (a.order.length !== b.order.length) return false;
  for (let i = 0; i < a.order.length; i++) {
    if (a.order[i] !== b.order[i]) return false;
  }
  for (const id of a.order) {
    if (a.sectionsById[id] !== b.sectionsById[id]) return false;
  }
  return true;
}

/** Move an array element to a new index in place, clamping the target into range. */
function reorderInPlace<T>(arr: T[], from: number, to: number): void {
  if (from < 0 || from >= arr.length) return;
  const clamped = Math.max(0, Math.min(to, arr.length - 1));
  if (clamped === from) return;
  const removed = arr.splice(from, 1);
  if (removed.length === 0) return;
  arr.splice(clamped, 0, removed[0]!);
}

// ── recursive block-tree helpers (the Shrine nested model: a block may host `blocks`) ──────────────

/** The located block + its sibling array (the parent container's blocks) + its parent block (null = section). */
interface BlockCtx {
  block: Block;
  siblings: Block[];
  parent: Block | null;
}

/** Find a block ANYWHERE in a section's tree by id (ids are unique nanoids), with its parent context. */
function findBlockCtx(section: SectionInstance, blockId: string): BlockCtx | null {
  function walk(siblings: Block[], parent: Block | null): BlockCtx | null {
    for (const b of siblings) {
      if (b.id === blockId) return { block: b, siblings, parent };
      if (b.blocks && b.blocks.length > 0) {
        const found = walk(b.blocks, b);
        if (found) return found;
      }
    }
    return null;
  }
  return walk(section.blocks, null);
}

/** Total blocks in a tree (for the section-wide maxBlocks cap, which counts every depth). */
function countBlocksDeep(blocks: Block[]): number {
  let n = 0;
  for (const b of blocks) {
    n += 1;
    if (b.blocks && b.blocks.length > 0) n += countBlocksDeep(b.blocks);
  }
  return n;
}

/** The schema for a block TYPE within a section (all types — incl. nested — live in def.schema.blocks). */
function blockSchemaByType(def: SectionDefinition, type: string) {
  return def.schema.blocks?.find((b) => b.type === type);
}

/**
 * Which child types a CONTAINER accepts (the Shrine `accepts` matrix):
 *  - section (parent = null): explicit `schema.accepts`, else legacy = all declared block types.
 *  - a block: its schema's `accepts` (empty = a leaf, accepts nothing).
 */
function acceptedTypes(def: SectionDefinition, parent: Block | null): string[] {
  if (!parent) {
    return def.schema.accepts
      ? [...def.schema.accepts]
      : (def.schema.blocks?.map((b) => b.type) ?? []);
  }
  const bs = blockSchemaByType(def, parent.type);
  return bs?.accepts ? [...bs.accepts] : [];
}

const EMPTY_META: DocMeta = {
  schemaVersion: 1,
  template: 'index' as TemplateKey,
  handle: 'default',
  locale: 'fr' as StoreLocale,
};

/**
 * Build the DEFAULT-THEME ("premium-calm") seed draft for a route. Used when there is no backend draft to
 * load (offline demo, or an unseeded route returning 404 PAGE_NOT_FOUND): instead of an empty canvas the
 * builder opens to the complete premium Home / Product theme assembled from the section presets
 * (@aquilixshop/ui-react themes/default). Client-side only — no backend, no persistence until first save.
 */
function seedDraftFor(route: BuilderRoute): { document: PageDocument; version: number } {
  // The shipped theme documents carry LITERAL identity: `ATLAS_PRODUCT_DOCUMENT` says `handle: "default"`
  // and every section/block id is written into the file. Seeding a route from it verbatim gave the builder
  // a document that claims to BE the store's `default` page — while the route says otherwise — and gave two
  // different templates the same section ids. Re-key to the route and mint fresh ids, once, here: every
  // caller (load's missing-draft fallback, prefetch, createTemplate) gets an honest document.
  const doc = buildDefaultDocument(route.template, localeOf(route));
  return {
    // `template` stays the one buildDefaultDocument resolved (it is the typed TemplateKey); only the
    // identity the shipped file hardcodes — the handle — and the locale are re-keyed to the route.
    document: reidDocument({ ...doc, handle: route.handle, locale: localeOf(route) }),
    version: 0,
  };
}

/** Narrow a route's free-string `locale` to a valid {@link StoreLocale} (defaults to 'fr'). */
/**
 * The language a document's TEXT is written in — the route's locale, else the store's language.
 *
 * NEVER use it as a DB row key. The row a page lives in is keyed by the STORE's language, and an absent
 * `route.locale` already means exactly that to the backend (0050 lot 0). Passing this collapsed value as
 * the route's locale is what made a published theme invisible on an Italian store (2026-08-20).
 */
function localeOf(route: BuilderRoute, storeLanguage?: string): StoreLocale {
  // Until 2026-09-13 every language other than fr/ar/en collapsed to `fr` here, because the theme
  // documents only shipped French (and one English) — so a German store opened Onyx in French. Themes
  // now carry a dictionary per store language (ui-react `themes/i18n`), applied by applyTheme() to the
  // seed; the language is the store's own, always.
  return route.locale || storeLanguage || 'fr';
}

/**
 * A 404 from getDraft = an unseeded route (PAGE_NOT_FOUND) → fall back to the default-theme seed.
 *
 * ONLY a 404. A `NetworkError` used to count too, and that was the bug the founder hit on 14/09/2026: the
 * backend blinked for a second, every caller below read "this page has never been seeded", and the builder
 * opened her product page on the shipped Atlas demo theme — `ready`, `clean`, no warning. Closing and
 * reopening the editor fixed it, which is the signature of a poisoned in-memory cache.
 *
 * The two are not the same fact and must never be conflated:
 *  - 404      — the server ANSWERED: there is no draft here. Seeding is honest.
 *  - network  — the server did not answer: we know NOTHING. Seeding invents a page.
 *
 * And inventing it is not merely cosmetic. `prefetch` writes the seed into `draftCache`, so the next
 * navigation shows the demo with no network call at all; `applyTheme` and the workspace→live publish both
 * fall back to "no existing version" and then SAVE, which overwrites a real page with the seed at
 * version 0. A blink of wifi must never be able to do that.
 *
 * A network failure now falls through to the caller's error path — `load()` already has one
 * (`loadState: 'error'`), and the prefetch already swallows anything that is not a missing draft.
 */
function isMissingDraftError(e: unknown): boolean {
  return e instanceof HttpError && e.status === 404;
}

/**
 * The page a NEW custom page is built ON TOP of (feature 0027 — "start from an existing page"). A route,
 * not a document: another custom page (`{template:'page', handle}`) or the home page
 * (`{template:'index', handle:'default'}`). `undefined` at the call site = the blank starter.
 */
export type NewPageSource = { template: string; handle: string };

/**
 * The starter document for a page created ON TOP of `source`: that route's CURRENT draft, re-keyed to the
 * `page` template with FRESH section/block ids, so the copy is independent of its source from the first
 * byte (ids are global keys — see {@link reidDocument}).
 *
 * Chrome is STRIPPED: the header/footer are store-global and composed around every page on load, so a
 * copied one would paint twice (an old pre-global-chrome row still carries them inline).
 *
 * An unseeded source (404 / offline) falls back to that route's default-theme seed, exactly like `load()`
 * — a seller who copies a never-saved home gets the home they SEE, not an error.
 */
/**
 * The document a NEW named template is copied from, when the source is NOT the page on screen: its saved
 * draft, or — for a source with no row yet (a template created moments ago, an unseeded type) — that
 * route's default-theme seed. Same fall-back as `load()` and `starterDocumentFrom`: a copy of a
 * never-saved page yields the page the seller SEES, never an error.
 *
 * Chrome is stripped for the same reason as `starterDocumentFrom`: header/footer are store-global and
 * composed around every page, so a copied one would paint twice.
 */
async function templateSourceDocument(route: BuilderRoute): Promise<PageDocument> {
  try {
    return stripChromeSections((await builderApi.getDraft(route)).document);
  } catch (e) {
    if (!isMissingDraftError(e)) throw e;
    return stripChromeSections(seedDraftFor(route).document);
  }
}

async function starterDocumentFrom(
  source: NewPageSource,
  locale: string | undefined,
  /** The OPEN document when `source` is the page being edited — copies what the seller SEES (unsaved edits
   *  included) instead of the server's last-saved draft. `null` → fetch. */
  live: PageDocument | null,
): Promise<PageDocument> {
  const route: BuilderRoute = { template: source.template, handle: source.handle, locale };
  let document: PageDocument;
  if (live) {
    document = live;
  } else {
    try {
      document = (await builderApi.getDraft(route)).document;
    } catch (e) {
      if (!isMissingDraftError(e)) throw e;
      document = seedDraftFor(route).document;
    }
  }
  // The new page's own identity is minted server-side (the handle is slugified from the title), so only
  // the template is re-keyed here; the doc's `handle` field is not load-bearing for custom pages.
  return reidDocument({ ...stripChromeSections(document), template: 'page' });
}

/**
 * Resolve the store's global theme for the load path (feature 0019). GET the theme, run the SOLE front
 * migrator, and NEVER throw: on any failure (no store yet / offline / session) fall back to the v2 Shrine
 * default so the builder always opens against a valid theme. The document GET reports its own load errors;
 * the theme is best-effort and must not block the editor from rendering.
 */
async function loadStoreTheme(): Promise<ThemeSettings> {
  try {
    return migrateThemeSettings(await builderApi.getTheme());
  } catch {
    return DEFAULT_THEME_SETTINGS;
  }
}

/**
 * Resolve the store's COD app config for the load path (feature 0026). Mirrors {@link loadStoreTheme}:
 * best-effort, NEVER throws — on any failure (no store / offline / no COD app installed) it falls back to
 * {@link DEFAULT_COD_CONFIG} so the canvas's `cod-form` block always previews a complete form. `getCodConfig`
 * classifies a `NO_ACTIVE_STORE` 409 to `NoStoreError`, which the document GET already surfaces separately —
 * here it just collapses to the default like every other failure.
 */
async function loadStoreCodConfig(): Promise<CodConfig> {
  try {
    return await builderApi.getCodConfig();
  } catch {
    return DEFAULT_COD_CONFIG;
  }
}

/** The optimistic-lock + publish envelope around a loaded document (from the API or a seed). */
interface LoadedDraft {
  document: PageDocument;
  version: number;
  publishedAt: string | null;
  hasUnpublishedChanges: boolean;
}

/** Does this badge map hold anything? `undefined` and `{}` both mean "no chip on this page". */
function hasBadges(badges: Readonly<Record<string, unknown>> | undefined): boolean {
  return badges !== undefined && Object.keys(badges).length > 0;
}

/**
 * The render-context page resources for a route + bound product (feature 0018 M2). On a `product` template
 * with a chosen preview product, the canvas binds that REAL product; otherwise it stays the 'home' demo
 * (the buy-box blocks then fall back to the demo product via resolveProduct).
 */
function pageResourcesFor(
  route: BuilderRoute,
  previewProduct: StorefrontProduct | null,
): RenderContext['page'] {
  if (route.template === 'product' && previewProduct) {
    return { routeType: 'product', product: previewProduct };
  }
  // Everything else used to answer `home`, which was harmless while nothing read the route TYPE — the two
  // readers in ui-react both test `=== 'product'`. The header now reads it too (it may float over a visual
  // on the home and nowhere else), so a collection template calling itself `home` made the canvas paint a
  // floating bar the live store would never serve. Only `index` is the home.
  //
  // LIMIT, written down rather than hidden: `collection` cannot be NAMED here. `PageResources` makes that
  // variant carry the collection and its products, and the editor has neither — it is editing the template,
  // not a collection. So a collection template previews as an ordinary `page`. It costs nothing today (a
  // collection template opens on `collection-banner`, which is not a full-bleed opener, so the bar never
  // floats there anyway) and the LIVE storefront passes the real `collection`.
  //
  // LIFTED (22/09/2026): a collection template DOES get named once a preview collection is bound — the
  // seller's real collection + its products, the exact shape the storefront passes (see `boundCollection`).
  if (route.template === 'collection' && boundCollection) {
    return {
      routeType: 'collection',
      collection: boundCollection.collection,
      products: [...boundCollection.products],
    };
  }
  return route.template === 'index' ? { routeType: 'home' } : { routeType: 'page' };
}

/**
 * The collection bound into the collection-template preview, with its storefront products. Module-level
 * rather than threaded through every `pageResourcesFor` caller: the render context is rebuilt from a dozen
 * places (theme edit, locale flip, route change, cache restore) and each of them must keep the binding —
 * reading it here is what makes that automatic. Written only by `bindPreviewCollection`.
 */
interface PreviewCollectionBinding {
  readonly collection: StorefrontCollection;
  readonly products: readonly StorefrontProduct[];
}
let boundCollection: PreviewCollectionBinding | null = null;
/** Test seam — forget the bound collection (module state survives a store reset otherwise). */
export function resetPreviewCollectionBinding(): void {
  boundCollection = null;
}

/**
 * Compose a page body with the store's live GLOBAL chrome into the one flat list the editor edits. Any
 * chrome the body still carries (a store not yet migrated, a draft restored from an old backup) is
 * DROPPED in favour of the global one — never rendered twice, never allowed back into the page.
 *
 * A `header`/`footer` route is passed through: there, the chrome IS the document being edited.
 */
function composeWithGlobalChrome(document: PageDocument): PageDocument {
  if (!wearsStoreChrome(document.template)) return document;
  const { header, footer } = chromeSections();
  return {
    ...document,
    sections: composeChromeSections(header, document.sections, footer),
  };
}

/**
 * The handle this route's chrome lives under — `'default'` for the whole store, or the workspace handle
 * while a theme is being staged (its chrome is staged with it and goes live on publishTheme).
 */
function chromeHandleOf(route: BuilderRoute): string {
  return chromeHandleFor(route.handle, isThemeWorkspaceHandle(route.handle));
}

/**
 * Carry an UNSAVED chrome edit onto the page the seller just landed on.
 *
 * The chrome is store-global while `dirty` / autosave are per page, so a header edited on the home and
 * left unsaved would otherwise sit in memory with no page dirty enough to flush it — and be lost on
 * reload. Marking the new page dirty (and scheduling its save) is what carries the edit to the server:
 * the save path writes the page row AND the chrome rows, so the header lands either way.
 */
function carryPendingChromeEdit(set: (fn: (s: BuilderState) => void) => void): void {
  if (!chromeHasPendingEdit()) return;
  set((s) => {
    s.dirty = true;
    s.saveState = 'unsaved';
  });
  scheduleAutosave();
}

/** Hand the hydrated chrome instances back to `globalChrome` (see `adoptHydratedChrome`). */
function adoptHydratedChromeFrom(hydrated: PageDocument): void {
  if (!wearsStoreChrome(hydrated.template)) return;
  const { header, footer } = splitChromeSections(hydrated.sections);
  adoptHydratedChrome(header, footer);
}

/** The page BODY only — what a per-page cache entry may hold (the chrome is store-global, not per page). */
function bodySectionsOf(order: string[], byId: Record<string, SectionInstance>): SectionInstance[] {
  return splitChromeSections(order.map((id) => byId[id]).filter((s): s is SectionInstance => !!s))
    .template;
}

/**
 * Resolve the chrome an unseeded store should open with: the ACTIVE theme's own header/footer documents,
 * falling back to the house theme. Used both for the 404 path (no chrome row yet) and the offline seed.
 */
async function themeChromeSeed(
  theme: ThemeSettings,
  locale: StoreLocale,
): Promise<{ header: SectionInstance[]; footer: SectionInstance[] }> {
  // ASYNC because a theme's documents are a dynamic import now (ui-react themes/registry.ts). This runs
  // only when the store has NO chrome row yet — the seed thunks below are called from the 404 branch —
  // so the common boot never touches a theme chunk, and the store that does still gets ITS OWN theme's
  // header rather than quietly falling back to Atlas's.
  const def = await loadThemeDefinition(theme?.presetId);
  const header = def?.documents?.header?.(locale) ?? buildDefaultHeaderDocument(locale);
  const footer = def?.documents?.footer?.(locale) ?? buildDefaultFooterDocument(locale);
  return { header: header.sections, footer: footer.sections };
}

/**
 * Load the store's global chrome for a route. NEVER throws: the page document reports its own load
 * errors, and a chrome fetch that fails must degrade to the theme seed rather than block the editor —
 * the seller can still work on the body.
 */
/**
 * Pull the workspace theme's payload chunk IN, on the async side of `load()`, so the synchronous
 * `syncThemeWorkspace()` pass that follows can read it from `peekThemePayload` in the same tick. A
 * non-workspace route resolves immediately and costs nothing.
 */
async function warmThemeWorkspacePayload(route: BuilderRoute): Promise<void> {
  if (!isThemeWorkspaceHandle(route.handle)) return;
  await loadThemePayload(themeIdOfWorkspaceHandle(route.handle));
}

async function loadChromeFor(
  route: BuilderRoute,
  theme: ThemeSettings,
  storeLanguage: string,
): Promise<void> {
  if (isChromeTemplate(route.template)) return; // editing the chrome document itself
  // The ROW the chrome lives in is keyed exactly like the page's: `route.locale`, absent = the store's
  // own language, resolved server-side. The collapsed fr/ar/en value only picks the SEED's text — using
  // it as the row key sent an English store's header edits into `fr` rows the storefront never serves.
  const rowLocale = route.locale;
  const seedLocale = localeOf(route, storeLanguage);
  const handle = chromeHandleOf(route);
  try {
    await ensureChromeLoaded(
      handle,
      rowLocale,
      async () => (await themeChromeSeed(theme, seedLocale)).header,
      async () => (await themeChromeSeed(theme, seedLocale)).footer,
    );
  } catch (e) {
    console.warn('[builder] global chrome failed to load — falling back to the theme seed.', e);
    const seed = await themeChromeSeed(theme, seedLocale);
    seedOfflineChrome(handle, rowLocale, seed.header, seed.footer);
  }
}

/**
 * Hydrate a loaded draft into the normalized store state (shared by the API-success path and the
 * default-theme seed fallback). Resets selection + history (loading is never an undoable edit) and marks
 * the page clean — a seeded draft is `dirty: false` so it doesn't autosave until the seller actually edits.
 */
/**
 * Leave the page currently open, the way `load()` does — because create/duplicate switch the route WITHOUT
 * going through `load()`, and used to skip both of its exit duties:
 *
 *  1. FLUSH the pending draft save. Autosave is debounced ~800ms; a seller who edits and immediately hits
 *     "+ New template" flipped the route before the timer fired, and the timer then wrote the NEW page
 *     under the NEW version — the edit to the page being left was simply dropped.
 *  2. SNAPSHOT it into the in-session cache, so switching back restores what was on screen.
 */
async function leaveOpenPage(get: () => BuilderState): Promise<void> {
  const prev = get();
  if (prev.loadState !== 'ready') return;
  if (prev.dirty) await flushSave('route-leave');
  const now = get();
  draftCache.set(routeKeyOf(now.route), snapshotStateToEntry(now));
}

/**
 * Publish a template the seller has just created or duplicated, immediately.
 *
 * The founder's call: "chaque template créé va être publié directement, j'aime pas cette notion de
 * template brouillon". And it costs nothing publicly — a brand-new handle has NO product assigned to it
 * (assignment is a separate, explicit act on the product), so publishing it changes not one page a
 * customer can reach. What it removes is the trap: assignment is PUBLISHED-only, so a draft-born template
 * could not be attached to anything until the seller found the Publish button and understood that it
 * meant this template.
 *
 * Deliberately `builderApi.publish` and NOT the autosave `publish()` helper: that one also publishes the
 * store's header/footer drafts, and creating a template must never push unrelated chrome edits live.
 *
 * A failure is swallowed on purpose — the template EXISTS (the draft save already succeeded), so throwing
 * would report "could not create the template" about a template that is right there. It simply stays a
 * draft, `hasUnpublishedChanges` stays true, and the selector offers "Publier ce template".
 */
async function publishNewTemplate(
  set: (fn: (s: BuilderState) => void) => void,
  route: BuilderRoute,
): Promise<void> {
  try {
    await builderApi.publish(route);
    set((s) => {
      s.publishedAt = new Date().toISOString();
      s.hasUnpublishedChanges = false;
    });
  } catch {
    // Left as a draft, honestly badged. See above.
  }
}

async function applyLoadedDocument(
  set: (fn: (s: BuilderState) => void) => void,
  route: BuilderRoute,
  draft: LoadedDraft,
  previewProduct: StorefrontProduct | null,
  theme: ThemeSettings,
  codConfig: CodConfig,
  storeCountry: string,
  storeCurrency: string,
  storeLanguage: string,
  previewOffer: ProductActiveOffer | null,
  /** The store's single payment method (`null` = unknown) — see `BuilderState.storePaymentMethod`. */
  paymentMethod: 'CARD' | 'COD' | null,
): Promise<void> {
  // hydrate: fill defaults + lazy-migrate + keep unknown section types (build-plan §5.6).
  // The document handed to the editor is the page body COMPOSED with the store's global chrome, so the
  // seller edits a complete page — and edits the ONE header every page shows (see globalChrome.ts).
  const hydrated = hydrateDocument(composeWithGlobalChrome(draft.document), sectionRegistry);
  // THE OTHER PAGES' BUY-BOXES (05/10/2026), asked for alongside the chunks below: the header's "Show cart"
  // switch is a STORE-wide question (see state/cartMode.ts), and this page only knows its own. A failure is
  // "unknown" → the switch stays locked; it is awaited before the first paint so it never flips afterwards.
  const elsewherePromise: Promise<readonly BuyBoxJourney[] | null> = (async () => {
    try {
      return await builderApi.getBuyBoxJourneys(route);
    } catch {
      return null;
    }
  })();
  // THE SHOP'S DATA BEFORE THE FIRST PAINT (22/09/2026). « Je vois quelque chose de statique dans les deux
  // pages avant de me montrer mes collections » : the canvas used to paint the demo collection / the demo
  // collection cards, then swap them for the seller's once a fetch landed. Both fetches now run alongside
  // the section chunks and are awaited with them, so the first frame is already hers — no demo flash.
  //  - a `collection` template binds its preview collection (remembered, else the store's first);
  //  - a `collection-list` showing "all my collections" gets the store list injected into the context.
  const store = useBuilderStore.getState();
  const wantsStoreCollections = hydrated.sections.some(
    (sec) => sec.type === 'collection-list' && sec.settings.source !== 'selected',
  );
  // THE await the lazy registry asks for, and the one place it has to be: every document that reaches the
  // canvas passes through here, so fetching this page's section components before the state flips is what
  // makes the first paint complete. Composed, so the header and footer count. Ten parallel chunk requests
  // instead of sixty-one sections in the entry bundle — and no section pops in after the page is up.
  await Promise.all([
    loadSectionComponents(hydrated.sections.map((s) => s.type)),
    route.template === 'collection'
      ? store.ensurePreviewCollection(route, { repaint: false })
      : Promise.resolve(),
    wantsStoreCollections ? store.loadCollectionsCatalog() : Promise.resolve(),
  ]);
  adoptHydratedChromeFrom(hydrated);
  // THE STORE'S FACTS, READ AGAIN NOW (24/09/2026). `loadStore()` runs alongside this load and may land
  // during the await above — after the caller read `storePaymentMethod` (and country, currency, language)
  // as « not known yet », and while `loadState` is still 'loading', so `loadStore`'s own context rebuild
  // (which only fires once the page is 'ready') never happens either. The canvas then painted the COD order
  // form on a CARD store until the seller reloaded again or touched a setting: « je dois rafraîchir plusieurs
  // fois ». What the store has answered by the time the context is built is what the context gets.
  // Every caller hands over the store's fields as they were when the load began; a value that landed since
  // wins, and one that never landed leaves the caller's in place (the same field, read earlier).
  const live = useBuilderStore.getState();
  const liveCountry = live.storeState === 'loaded' ? live.storeCountry : storeCountry;
  const liveCurrency = live.storeState === 'loaded' ? live.storeCurrency : storeCurrency;
  const liveLanguage = live.storeState === 'loaded' ? live.storeLanguage : storeLanguage;
  const livePaymentMethod = live.storePaymentMethod ?? paymentMethod;
  // The resolved store theme drives the canvas from the first paint (feature 0019 finding #4). Because
  // this sets dirty:false, loading the real theme BEFORE any edit is what stops a first theme edit from
  // overwriting the seller's stored theme with defaults. The COD config + selling country ride alongside
  // (features 0026 / 0026-06) so the buy-box cod-form previews the seller's country-correct form.
  const builtContext = buildEditContext(
    // The STORE's language, not the document's own field — the two can disagree (see storeLanguage).
    localeOf(route, liveLanguage) || hydrated.locale,
    pageResourcesFor(route, previewProduct),
    theme,
    codConfig,
    liveCountry,
    liveCurrency,
    previewOffer ?? undefined,
    livePaymentMethod ?? undefined,
  );
  // The store's collections, already in the context the FIRST frame reads (see above). The canvas hook
  // `useResolvedCollections` re-sets the same list afterwards — same data, no visible change.
  const context: RenderContext = wantsStoreCollections
    ? {
        ...builtContext,
        resolved: {
          ...builtContext.resolved,
          storeCollections: useBuilderStore.getState().collectionsCatalog,
        },
      }
    : builtContext;
  const buyBoxJourneysElsewhere = await elsewherePromise;
  set((s) => {
    s.route = route;
    // Set in the SAME write as the route: a stale list from the previous page must never answer for this one.
    s.buyBoxJourneysElsewhere = buyBoxJourneysElsewhere;
    // Chrome pinned to its rail group on the way in — a page whose header/footer drifted into the body
    // (see sectionZones.ts) is repaired on open instead of rendering chrome in the middle of the page.
    s.order = orderWithChromePinned(hydrated.sections);
    s.sectionsById = keyById(hydrated.sections);
    s.meta = pickMeta(hydrated);
    s.renderContext = context;
    s.themeSettings = theme;
    s.codConfig = codConfig;
    s.previewOffer = previewOffer;
    s.themeSaveState = 'idle';
    // Direction split (founder feedback 2026-07-04): the builder CHROME (rail/topbar/inspector) is ALWAYS
    // LTR — flipping the whole editor to RTL for an Arabic store was confusing. Only the PREVIEW flips:
    // `context.dir` (→ `renderContext.dir`) is seeded from the store locale here (Arabic → RTL), drives the
    // canvas `[data-aqx-page]` + every rendered section, and the seller can flip it via `togglePreviewDir`.
    s.serverVersion = draft.version;
    s.publishedAt = draft.publishedAt;
    s.hasUnpublishedChanges = draft.hasUnpublishedChanges;
    s.selectedSectionId = null;
    s.selectedBlockId = null;
    s.focusedFieldId = null;
    // Loading is NOT an edit — reset history + coalescing entirely.
    s.past = [];
    s.future = [];
    s._lastMergeKey = null;
    s._lastEditAt = 0;
    s.dirty = false;
    s.saveState = 'idle';
    s.loadState = 'ready';
  });
}

// ── In-session page cache (feature 0021): instant, network-free template switching ──────────────────────
// The seller mostly toggles between a few pages (Home ↔ Product). We keep each visited/prefetched page's
// normalized editor state in a module Map, snapshot the page being LEFT on every switch (so unsaved edits
// survive a round-trip and switching back is instant AND lossless), and restore a cached page synchronously
// (no `loadState:'loading'`, no network on the critical path). The cache is authoritative for THIS single-
// user session — every leave-snapshot overwrites the entry with the latest state, so it never goes stale
// within the session (cross-tab freshness is a separate, deferred concern).
interface DraftCacheEntry {
  order: string[];
  sectionsById: Record<string, SectionInstance>;
  meta: DocMeta;
  serverVersion: number | null;
  publishedAt: string | null;
  hasUnpublishedChanges: boolean;
  dirty: boolean;
}
const draftCache = new Map<string, DraftCacheEntry>();
const prefetchInFlight = new Set<string>();
/** Whether the store theme has been fetched at least once this session (it's global — never re-fetch on a
 *  page switch; the live in-memory `themeSettings` already reflects any edits). */
let themeEverLoaded = false;
/** Same as `themeEverLoaded` but for the store's COD config (feature 0026): store-global, fetched once. */
let codConfigEverLoaded = false;

function routeKeyOf(r: BuilderRoute): string {
  return `${r.template}|${r.handle}|${r.locale}`;
}

/** Test-only (called by resetStore): clear the page cache + theme flag so one test never leaks into the next. */
export function __resetPageCaches(): void {
  draftCache.clear();
  prefetchInFlight.clear();
  themeEverLoaded = false;
  codConfigEverLoaded = false;
  workspaceDemoProductId = null;
  previewReviewsCache.clear();
  previewReviewsInFlight.clear();
  resetGlobalChrome();
}

/**
 * Normalize a fetched/seeded draft response into a clean cache entry (for prefetch + fetch-populate).
 *
 * BODY ONLY. A cache entry must never hold chrome: the chrome is store-global and lives in
 * `globalChrome`, so a page restored from the cache re-composes the CURRENT header. Caching the chrome
 * per page is exactly how a header edit on the home would fail to show on the product page — the
 * in-session version of the very drift this change removes.
 */
function entryFromDraft(draft: LoadedDraft): DraftCacheEntry {
  const hydrated = hydrateDocument(stripChromeSections(draft.document), sectionRegistry);
  return {
    order: orderWithChromePinned(hydrated.sections),
    sectionsById: keyById(hydrated.sections),
    meta: pickMeta(hydrated),
    serverVersion: draft.version,
    publishedAt: draft.publishedAt,
    hasUnpublishedChanges: draft.hasUnpublishedChanges,
    dirty: false,
  };
}

/** Snapshot the CURRENT (possibly-edited) editor state into a cache entry — body only, see above. */
function snapshotStateToEntry(s: BuilderState): DraftCacheEntry {
  const body = wearsStoreChrome(s.meta.template)
    ? bodySectionsOf(s.order, s.sectionsById)
    : s.order.map((id) => s.sectionsById[id]).filter((x): x is SectionInstance => !!x);
  return {
    order: body.map((sec) => sec.id),
    sectionsById: keyById(body),
    meta: s.meta,
    serverVersion: s.serverVersion,
    publishedAt: s.publishedAt,
    hasUnpublishedChanges: s.hasUnpublishedChanges,
    dirty: s.dirty,
  };
}

/** Restore a cached page INSTANTLY (no network, no loading state) — rebuilds the render context against the
 *  current live theme + bound preview product, resets selection/history, and preserves the entry's dirty. */
function restoreFromCache(
  set: (fn: (s: BuilderState) => void) => void,
  route: BuilderRoute,
  entry: DraftCacheEntry,
  theme: ThemeSettings,
  previewProduct: StorefrontProduct | null,
  codConfig: CodConfig,
  storeCountry: string,
  storeCurrency: string,
  storeLanguage: string,
  previewOffer: ProductActiveOffer | null,
  /** The store's single payment method (`null` = unknown) — see `BuilderState.storePaymentMethod`. */
  paymentMethod: 'CARD' | 'COD' | null,
): void {
  const context = buildEditContext(
    localeOf(route, storeLanguage),
    pageResourcesFor(route, previewProduct),
    theme,
    codConfig,
    storeCountry,
    storeCurrency,
    previewOffer ?? undefined,
    paymentMethod ?? undefined,
  );
  // Re-compose the cached BODY with the store's CURRENT global chrome: a header edited on the page we
  // just left must already be there when this one restores. The cache holds no chrome of its own.
  const composed = wearsStoreChrome(entry.meta.template)
    ? composeChromeSections(
        chromeSections().header,
        entry.order.map((id) => entry.sectionsById[id]).filter((x): x is SectionInstance => !!x),
        chromeSections().footer,
      )
    : entry.order.map((id) => entry.sectionsById[id]).filter((x): x is SectionInstance => !!x);
  set((s) => {
    s.route = route;
    s.order = composed.map((sec) => sec.id);
    s.sectionsById = keyById(composed);
    s.meta = entry.meta;
    s.renderContext = context;
    s.themeSettings = theme;
    s.codConfig = codConfig;
    s.previewOffer = previewOffer;
    // Preview direction (`context.dir`) is re-seeded from the store locale; the chrome stays LTR. See load().
    s.serverVersion = entry.serverVersion;
    s.publishedAt = entry.publishedAt;
    s.hasUnpublishedChanges = entry.hasUnpublishedChanges;
    s.selectedSectionId = null;
    s.selectedBlockId = null;
    s.focusedFieldId = null;
    s.past = [];
    s.future = [];
    s._lastMergeKey = null;
    s._lastEditAt = 0;
    s.dirty = entry.dirty;
    s.saveState = entry.dirty ? 'unsaved' : 'idle';
    s.loadState = 'ready';
  });
}

/**
 * Persist the chosen PREVIEW PRODUCT (feature 0018/0021) across reloads, SCOPED BY STORE. `previewProduct` is
 * in-memory only, so a product-template you were designing against would reset to the built-in demo on every
 * reload. We stash the FULL StorefrontProduct (a few KB) — not just its id — so a cold reload can hydrate the
 * canvas AND the selector label SYNCHRONOUSLY from cache (no demo-product flash) and still works offline;
 * `load()` then revalidates the snapshot against the API in the background. The key is namespaced by the JWT
 * `store_id` claim so an agency editing several stores in one browser never restores store A's product onto
 * store B (getStoreId is a read-only display/scoping convenience — the backend stays the tenancy authority).
 * Best-effort: storage may be disabled (private mode) and the snapshot may be stale (edited/deleted) — both
 * are non-fatal (the preview falls back to the demo / gets corrected on revalidation).
 */
const PREVIEW_PRODUCT_KEY_BASE = 'aqx-builder-preview-product';
function previewProductKey(): string {
  return `${PREVIEW_PRODUCT_KEY_BASE}:${getStoreId() ?? 'default'}`;
}

/**
 * THEME WORKSPACES (spec 0032 — "one draft PER theme", Shopify draft-themes model). Every applied
 * theme edits its OWN set of draft pages under a reserved handle (`theme-ws-<themeId>`, kebab-safe
 * for the backend's slug rules) via the EXISTING named-templates storage (0018) — so re-applying a
 * theme REOPENS the seller's customizations instead of overwriting anything, and the live pages
 * (handle 'default') are only touched by an explicit theme publish (copy → publish).
 */
export const THEME_WORKSPACE_PREFIX = 'theme-ws-';
export function themeWorkspaceHandle(themeId: string): string {
  return `${THEME_WORKSPACE_PREFIX}${themeId}`;
}
export function isThemeWorkspaceHandle(handle: string): boolean {
  return handle.startsWith(THEME_WORKSPACE_PREFIX);
}
export function themeIdOfWorkspaceHandle(handle: string): string {
  return handle.slice(THEME_WORKSPACE_PREFIX.length);
}

/**
 * Fingerprint of a workspace document, ignoring the stamp itself and the envelope keys applyTheme
 * rewrites per store (handle/locale). Stable across sessions — it is only ever compared to another
 * fingerprint computed the same way, never persisted as a version number.
 */
export function themeSeedSignature(doc: PageDocument): string {
  const meta = { ...doc.meta };
  delete meta.themeSeed; // the stamp can never be part of what it fingerprints
  const payload = JSON.stringify({ sections: doc.sections, meta, template: doc.template });
  // FNV-1a — short, dependency-free, and collisions here would only mean "refuse to refresh".
  let h = 0x811c9dc5;
  for (let i = 0; i < payload.length; i++) {
    h ^= payload.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `${h.toString(16)}-${payload.length.toString(36)}`;
}

/** Stamp a freshly-built theme document so a later apply can recognise it as an untouched seed. */
export function stampThemeSeed(doc: PageDocument): PageDocument {
  return { ...doc, meta: { ...doc.meta, themeSeed: themeSeedSignature(doc) } };
}

/**
 * Is this stored workspace draft still EXACTLY what a previous apply seeded? True only when its stamp
 * matches its own current content — one seller edit breaks the match forever, which is precisely the
 * guarantee we want before overwriting. Unstamped drafts (seeded before this stamp existed) are
 * matched against `knownPastSeeds`, the fingerprints of the theme documents we have ever shipped.
 */
export function isPristineThemeSeed(
  doc: PageDocument,
  knownPastSeeds: readonly string[] = [],
): boolean {
  const sig = themeSeedSignature(doc);
  return doc.meta?.themeSeed ? doc.meta.themeSeed === sig : knownPastSeeds.includes(sig);
}

/**
 * The id of a theme demo product EPHEMERALLY bound by `syncThemeWorkspace` (spec 0032): ENTERING a
 * workspace whose theme ships a `demoProduct` (e.g. Volt → Aurio Open Pro) ALWAYS binds it — the
 * workspace is the theme's showroom, so it previews on-brand content even when the seller has a
 * preview product of their own (founder direction 2026-07-24: "opening Volt must show Volt"). The
 * seller's own preview is remembered in {@link sellerPreviewBeforeWorkspace} and restored on ANY
 * exit (navigation or theme publish). A product the seller explicitly picks WHILE INSIDE the
 * workspace is respected on subsequent in-workspace loads (only the entry transition rebinds).
 * The binding bypasses `persistPreviewProduct` (it must never become the seller's persisted 0018
 * choice). Module-level like `themeEverLoaded`; null = nothing ephemeral is bound.
 */
let workspaceDemoProductId: string | null = null;
/** The seller's own preview product at workspace ENTRY — restored (ephemerally) on exit. */
let sellerPreviewBeforeWorkspace: StorefrontProduct | null = null;

/**
 * Session cache of the preview products' reviews (feature 0066), keyed by product id — `null` is a CACHED
 * answer ("this product has no published review"), not a miss.
 *
 * It exists because the reviews used to be the LAST thing on the canvas to arrive and the FIRST thing to
 * vanish. The section's editing placeholder reads "0 reviews", so every moment the fetch is in flight the
 * canvas is actively telling the seller that her shop has none — and a re-bind of the SAME product (the
 * cold-boot revalidation, a template change, a return to a product route) used to blank the reviews and
 * pay for the round trip again, printing that lie a second and a third time.
 *
 * With the cache, a product's reviews are fetched ONCE per session: a re-bind repaints them synchronously,
 * and only a product never seen before waits on the network.
 */
const previewReviewsCache = new Map<string, ProductReviews | null>();
/** Product ids whose reviews call is in flight — a second bind must not open a second request. */
const previewReviewsInFlight = new Set<string>();

/**
 * Serial number of the newest `loadTemplateUsage()` call — see it for why an older answer is dropped
 * rather than merged. Module scope, like the caches above: it is about requests, not about a document.
 */
let templateUsageTicket = 0;

/**
 * Per-theme staged ThemeSettings persistence (spec 0032; moved SERVER-SIDE by 0034 Bloc 1): each
 * workspace's colors/typography edits must survive a refresh — the 0019 settings row is live-on-save
 * and single, so workspace settings can never be PUT before the theme publish. They now live under
 * `store_settings.data.themeWorkspaces[<themeId>]` via `saveThemeWorkspace.ts` instead of
 * localStorage, so the work survives a machine change AND the server can render an unpublished theme
 * in its own colors (which is what the storefront preview needs). Cleared by `publishTheme()`.
 */
function persistStagedTheme(themeId: string, settings: ThemeSettings | null): void {
  if (settings) stageThemeSettings(themeId, settings);
  else dropStagedTheme(themeId);
}
function persistPreviewProduct(product: StorefrontProduct | null): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (product) window.localStorage.setItem(previewProductKey(), JSON.stringify(product));
    else window.localStorage.removeItem(previewProductKey());
  } catch {
    /* storage disabled — the preview simply won't persist; not fatal. */
  }
}
export function readPersistedPreviewProduct(): StorefrontProduct | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(previewProductKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StorefrontProduct;
    return parsed && typeof parsed.id === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

const PREVIEW_COLLECTION_KEY_BASE = 'aqx-builder-preview-collection';
function previewCollectionKey(): string {
  return `${PREVIEW_COLLECTION_KEY_BASE}:${getStoreId() ?? 'default'}`;
}
/** Only the ID is remembered: the collection and its products are always re-fetched, never served stale. */
function persistPreviewCollectionId(id: string | null): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (id) window.localStorage.setItem(previewCollectionKey(), id);
    else window.localStorage.removeItem(previewCollectionKey());
  } catch {
    /* storage disabled — the preview simply won't persist; not fatal. */
  }
}
export function readPersistedPreviewCollectionId(): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(previewCollectionKey());
    return raw && raw.trim() ? raw : null;
  } catch {
    return null;
  }
}

export const useBuilderStore = create<BuilderState>()(
  immer((set, get) => {
    /**
     * The shared mutation wrapper (build-plan §5.2). Snapshots the PRE-mutation state onto `past`
     * (coalescing same-target settings bursts via `mergeKey`), runs the Immer draft mutation, then sets
     * dirty bookkeeping and clears `future` (a new edit forks history). One place so history + dirty
     * can never be forgotten. (Autosave scheduling hooks in here in M3.4.)
     */
    function commit(mut: (s: BuilderState) => void, mergeKey?: string): void {
      // SUPERADMIN CONSULTATION — the one lock for the whole document (founder 25/10/2026: « en mode
      // consultation on doit pas pouvoir modifier ou rajouter »). Every structural reducer (insert /
      // move / remove / duplicate / hide) and every settings reducer funnels through here, so one gate
      // closes them all — including the paths no greyed button could ever cover: a canvas drag, a field
      // typed in the inspector, a block picked from a menu that slipped through. The greyed triggers are
      // the SIGN; this is the lock. Saving was already blocked (autosave / theme save / http), but a
      // blocked save still left the admin looking at a page that no longer matched the seller's.
      if (isReadOnlyView()) return;
      // Capture the PRE-mutation snapshot from the COMMITTED state (via get()), NOT from inside the
      // Immer producer. Snapshotting a draft reference is a trap: Immer finalizes that reference to the
      // POST-mutation value, so the undo frame would wrongly hold the new value. The committed state is
      // already structurally shared (untouched sections are the same object refs), so this is cheap.
      const pre = snapshot(get());
      set((s) => {
        pushHistory(s, mergeKey, pre);
        mut(s);
        s.dirty = true;
        s.saveState = 'unsaved';
        s.future = [];
      });
      // Schedule the debounced PUT OUTSIDE the Immer producer (build-plan §5.2/§5.4). One place so an
      // autosave can never be forgotten for any mutation that goes through the choke-point.
      scheduleAutosave();
    }

    /**
     * After undo/redo restored a DIFFERENT theme (feature 0019): rebuild the render context so the canvas
     * re-themes, and persist the reverted theme through the dedicated channel (live-on-save). The context
     * rebuild uses the object-set form (readonly theme + function-bearing context don't fit an Immer draft
     * slot — same reason as setPreviewProduct).
     */
    function applyRestoredTheme(): void {
      const st = get();
      const context = buildEditContext(
        localeOf(st.route, st.storeLanguage),
        pageResourcesFor(st.route, st.previewProduct),
        st.themeSettings,
        st.codConfig,
        st.storeCountry,
        st.storeCurrency,
        st.previewOffer ?? undefined,
        st.storePaymentMethod ?? undefined,
      );
      set({ renderContext: context });
      scheduleThemeSave();
    }

    /**
     * Revalidate a storage-RESTORED preview product against the API (feature 0021). `load()` hydrates the
     * canvas from the cached snapshot synchronously (no flash); this then confirms it in the background:
     * a fresh copy swaps in (catches an edit made elsewhere), a 404 drops it back to the demo (deleted /
     * archived product), and any other failure (offline / 5xx) keeps the cached snapshot so the seller
     * keeps designing. Guards against races: only applies if still on the product route AND still bound to
     * the SAME product (the seller may have navigated or picked another meanwhile).
     *
     * [followTemplate] moves the editor onto the product's OWN template when the two disagree. That belongs
     * to a COLD BOOT only (see the call sites): there the route came from the URL, which is not an address
     * the seller chose. On a load she DID choose — clicking a layout in the selector — following it back is
     * the bug of 19/09/2026: every named template the product is not assigned to snapped straight back to
     * the one it is, so the click read as dead and the "Apply to this product" button inside that template
     * (the only way to assign it) could never be reached.
     */
    async function revalidatePreviewProduct(followTemplate = false): Promise<void> {
      const current = get().previewProduct;
      if (!current) return;
      // The workspace's EPHEMERAL theme demo is not a catalog product — revalidating it would 404 and
      // unbind it right after `syncThemeWorkspace` bound it. Skip; it never persists, so never goes stale.
      if (workspaceDemoProductId !== null && current.id === workspaceDemoProductId) return;
      try {
        const fresh = await catalogApi.getProduct(current.id);
        if (get().route.template === 'product' && get().previewProduct?.id === current.id) {
          // Refresh the product's REAL template alongside the product itself. The bound product is restored
          // from storage on boot and only revalidated here, so skipping this would leave the selector
          // asserting "ce produit utilise Default" about a product actually assigned elsewhere.
          set((s) => {
            s.previewProductTemplate = fresh.templateSuffix ?? null;
            s.previewProductTemplateKnown = true;
          });
          get().setPreviewProduct(productResponseToStorefront(fresh));
          // FOLLOW THE PRODUCT ON A COLD BOOT TOO. The route came from the URL, which is not an address the
          // seller chose — it is whatever the store→URL mirror last wrote. Restoring the remembered product
          // without it left exactly the state that cost a day: the canvas showing one product while the
          // editor held another product's page ("Utilise cellilute" next to an open `Default`). What she
          // sees and what she edits must be the same page, on the first paint as much as on a pick.
          const route = get().route;
          const wanted = fresh.templateSuffix?.trim() || 'default';
          if (followTemplate && !isThemeWorkspaceHandle(route.handle) && wanted !== route.handle) {
            await get().load({ ...route, handle: wanted });
          }
        }
      } catch (e) {
        if (e instanceof HttpError && e.status === 404 && get().previewProduct?.id === current.id) {
          set((s) => {
            s.previewProductTemplate = null;
            s.previewProductTemplateKnown = false;
          });
          get().setPreviewProduct(null);
        }
      }
    }

    /**
     * Fetch the bound preview product's active quantity offer (feature 0026-07) and thread it into the render
     * context so the buy-box `quantity` block previews the seller's offer TILES. Best-effort + FAIL-OPEN: no
     * product / no offer / any failure → `previewOffer: null` (the classic stepper renders; the canvas never
     * crashes) — `catalogApi.getActiveOffer` already collapses a 404 / error to null. Guards against a race:
     * only applies if the store is STILL bound to the SAME product when the fetch resolves (the seller may have
     * switched products meanwhile). The context is rebuilt off the COMMITTED state (readonly offer + function-
     * bearing context don't fit an Immer draft slot — same reason as setPreviewProduct / applyRestoredTheme).
     */
    async function refreshPreviewOffer(product: StorefrontProduct | null): Promise<void> {
      if (!product) return; // the demo (null) already cleared previewOffer synchronously in setPreviewProduct
      const offer = activeOfferForProduct(product, await catalogApi.getActiveOffer(product.id));
      const cur = get();
      if (cur.previewProduct?.id !== product.id) return; // switched away — drop this stale result
      const context = buildEditContext(
        localeOf(cur.route, cur.storeLanguage),
        pageResourcesFor(cur.route, cur.previewProduct),
        cur.themeSettings,
        cur.codConfig,
        cur.storeCountry,
        cur.storeCurrency,
        offer ?? undefined,
        cur.storePaymentMethod ?? undefined,
      );
      set({ previewOffer: offer, renderContext: context });
    }

    /**
     * Fetch the bound preview product's published reviews (feature 0066) so the `product-reviews` section
     * and the `rating` block on "My real reviews" paint the shop's actual numbers on the canvas.
     *
     * Same posture as {@link refreshPreviewOffer}: best-effort, fail-open, and guarded against the race
     * where the seller switches product while the call is in flight. It does NOT rebuild the render
     * context — the canvas merges `previewReviews` in as it renders, so a later context rebuild cannot
     * drop them.
     */
    async function refreshPreviewReviews(
      product: StorefrontProduct | null,
      opts?: { readonly retryEmpty?: boolean },
    ): Promise<void> {
      if (!product) return; // the demo (null) already cleared previewReviews synchronously below
      const id = product.id;
      /*
        An EXPLICIT gesture on an EMPTY band re-asks (`retryEmpty`): adding the section, or clicking it.

        «0 avis» is also exactly what the canvas shows while an import she ran in the other tab has
        already finished — the cached "none" is an answer from BEFORE the import, and nothing short of
        reloading the editor replaced it. Costs one small GET, and only when there is nothing on screen
        to lose: a band already showing reviews is never re-fetched.
      */
      if (opts?.retryEmpty && previewReviewsCache.get(id) === null) previewReviewsCache.delete(id);
      // Answered once per session (including the cached "none"): a re-bind of the same product repaints
      // from `previewReviewsCache` in `bindPreviewProduct` and never reaches this call at all. A FAILURE
      // is not in that cache, so it is re-asked instead of being served back forever.
      if (previewReviewsCache.has(id) || previewReviewsInFlight.has(id)) return;
      previewReviewsInFlight.add(id);
      /*
        SAY "we are asking" HERE, not only in `bindPreviewProduct`.

        The boot call (top of `load()`) fires BEFORE any bind: the product came out of storage a few lines
        earlier, so nothing had raised the flag, and the canvas painted «0 avis» for the whole of that
        first round trip — the exact false sentence the flag exists to prevent, on the one paint the
        seller always sees. Raised where the request actually starts, both callers are covered.
      */
      if (get().previewProduct?.id === id) {
        set({ previewReviewsLoading: true, previewReviewsError: null });
      }
      let answer: PreviewReviewsAnswer;
      try {
        answer = await catalogApi.getStorefrontReviews(id);
      } finally {
        previewReviewsInFlight.delete(id);
      }
      const payload = answer.status === 'ok' ? (parseProductReviews(answer.payload) ?? null) : null;
      /*
        An answer we could not READ is a failure too, not an empty shop: `parseProductReviews` returning
        null on a 200 means the payload was not the shape we know (a proxy's error page, a truncated
        body), and printing «0 avis» over it is the same lie as printing it over a dead backend.
      */
      const failure: PreviewReviewsFailure | null =
        answer.status === 'failed'
          ? answer.reason
          : answer.status === 'ok' && payload === null
            ? 'server'
            : null;
      // A FAILURE IS NEVER CACHED. Caching it would freeze the wrong sentence on the canvas for the rest
      // of the session: no re-bind, no product switch, no return to the route would ever ask again.
      if (failure === null) previewReviewsCache.set(id, payload);
      if (get().previewProduct?.id !== id) return; // switched away — drop this stale result
      set({ previewReviews: payload, previewReviewsLoading: false, previewReviewsError: failure });
    }

    /**
     * Bind a product into the preview render context (the body of `setPreviewProduct` — see its doc
     * for the full semantics). `persist` controls the ONE difference between the two callers: the
     * seller's explicit choice (`setPreviewProduct`) is remembered across reloads via
     * `persistPreviewProduct`, while the theme-workspace demo binding (`syncThemeWorkspace`, spec
     * 0032) is EPHEMERAL — it must never overwrite the seller's persisted 0018 slot, so a reload
     * outside the workspace can never resurrect the theme's demo product.
     */
    function bindPreviewProduct(product: StorefrontProduct | null, persist: boolean): void {
      // Binding a DIFFERENT product (or clearing it) invalidates any previously-fetched quantity offer; keep
      // the offer only if it still belongs to the SAME product id (0026-07). The fresh offer for a new product
      // is fetched asynchronously by refreshPreviewOffer below — the tiles appear once it resolves.
      const prevOffer = get().previewOffer;
      const offer = product && prevOffer?.productId === product.id ? prevOffer : null;
      // Same rule for the reviews (0066), read from the session cache rather than from the previous
      // binding: re-binding the SAME product (cold-boot revalidation, template change, a return to a
      // product route) must repaint its reviews on the spot. Blanking them here is what made the canvas
      // print "0 reviews" over a shop with four hundred, once per navigation, for a whole round trip.
      const reviews = product ? (previewReviewsCache.get(product.id) ?? null) : null;
      // Asked-for-but-not-answered is exactly "bound to a product the cache has never answered for". The
      // demo (no product) is not loading: nothing will ever be fetched for it.
      const reviewsLoading = product ? !previewReviewsCache.has(product.id) : false;
      const context = buildEditContext(
        localeOf(get().route, get().storeLanguage),
        pageResourcesFor(get().route, product),
        // Preserve the store's resolved theme — binding a product must NOT reset the canvas to the
        // default theme (regression fix: buildEditContext defaults the theme when it isn't passed). (0021)
        get().themeSettings,
        // Same for the COD config — binding a product must not drop the seller's cod-form back to default.
        get().codConfig,
        get().storeCountry,
        get().storeCurrency,
        offer ?? undefined,
        get().storePaymentMethod ?? undefined,
      );
      if (persist) persistPreviewProduct(product);
      // A product whose reviews are NOT in the cache paints none until its fetch lands: they belong to a
      // product that is no longer bound, and leaving them up shows one product's reviews under another's.
      set({
        previewProduct: product,
        previewOffer: offer,
        previewReviews: reviews,
        previewReviewsLoading: reviewsLoading,
        // A new binding re-asks (nothing cached) or repaints from cache: either way the PREVIOUS
        // product's failure is not a statement about this one.
        previewReviewsError: null,
        renderContext: context,
      });
      // Fetch THIS product's active quantity offer in the background (0026-07) so the buy-box `quantity` block
      // previews the seller's offer tiles instead of the classic stepper. Fail-open, and a no-op on the demo.
      void refreshPreviewOffer(product);
      // ...and its reviews (0066), for the same reason: the canvas must show the shop, not a placeholder.
      void refreshPreviewReviews(product);
    }

    return {
      order: [],
      sectionsById: {},
      meta: EMPTY_META,
      route: DEFAULT_ROUTE,
      renderContext: null,
      previewProduct: null,
      countdownBar: null,
      countdownPopup: null,
      selectedCountdown: null,
      previewOffer: null,
      previewReviews: null,
      previewReviewsLoading: false,
      previewReviewsError: null,
      catalog: [],
      catalogState: 'idle',
      previewCollection: null,
      previewCollectionState: 'idle',
      collectionsCatalog: [],
      collectionsCatalogState: 'idle',
      templateUsage: null,
      previewProductTemplate: null,
      previewProductTemplateKnown: false,
      storeSlug: null,
      storePrimaryDomain: null,
      storeName: '',
      storeCountry: '',
      storeLanguage: '',
      storeCurrency: 'MAD',
      storePaymentMethod: null,
      buyBoxJourneysElsewhere: null,
      storeState: 'idle',

      themeSettings: DEFAULT_THEME_SETTINGS,
      codConfig: DEFAULT_COD_CONFIG,
      themeSaveState: 'idle',
      themeStagedForPublish: false,
      liveThemeSettings: DEFAULT_THEME_SETTINGS,
      themeSavedAt: null,

      serverVersion: null,
      publishedAt: null,
      hasUnpublishedChanges: false,

      selectedSectionId: null,
      selectedBlockId: null,
      focusedFieldId: null,

      past: [],
      future: [],
      _lastMergeKey: null,
      _lastEditAt: 0,

      dirty: false,
      saveState: 'idle',
      saveFailure: null,
      lastSavedAt: null,
      loadState: 'idle',

      device: readPersistedDevice(),
      previewMode: false,
      selectMode: false,
      revealNonce: 0,

      async load(route = get().route) {
        const prev = get();
        const fromKey = routeKeyOf(prev.route);
        const toKey = routeKeyOf(route);
        // COLD BOOT = there is no editor on screen yet, so this route came from the URL and not from a
        // seller's click. Only then may the preview-product revalidation move the editor onto the
        // product's own template (`revalidatePreviewProduct`'s `followTemplate`): a navigation she made
        // herself — a layout picked in the selector — must stand, or the template she wants to assign
        // can never be opened (founder, 19/09/2026).
        const coldBoot = prev.loadState !== 'ready';
        // ENTERING the product page from another page is the same situation (founder 05/10/2026): the handle
        // in `route` is nobody's choice — the page switcher's "Product" row names the TYPE, not a layout, and
        // the Layout list (the one place a layout is picked on purpose, 19/09/2026 above) is only reachable
        // FROM a product page. So the product on screen owns the route here too. Her remembered Move Shift
        // renders `move-shift`; "Product" from Home opened `default`, a page that product never renders, and
        // the cart-mode change she published there changed nothing on her storefront.
        const followProduct =
          coldBoot || (route.template === 'product' && prev.route.template !== 'product');

        // An EXPLICIT `?locale=` names a language the store's own may not be (the languages page opens a
        // page in another language), and `loadStore()` only fetched the store's. Ask for this one too —
        // idempotent, already-resolved for the usual case where the two agree, and it must land before the
        // document is rendered or the page paints in French and snaps.
        if (route.locale) await ensureStoreDictionary(route.locale);

        // Snapshot the page we're LEAVING into the in-session cache (feature 0021) so a later switch back is
        // INSTANT and lossless — it captures any unsaved edits, not just the last saved version. Every leave
        // overwrites the entry, so the cache never goes stale within the session.
        if (prev.loadState === 'ready' && fromKey !== toKey) {
          // PERSIST before leaving. The cache keeps unsaved edits for THIS session, but a seller who
          // switches page and closes the tab would lose them — and now that picking a product navigates,
          // leaving happens far more often. `flushSave` no-ops when nothing is dirty, and its identity
          // guard still applies: it writes to the page being left, or refuses.
          if (prev.dirty) await flushSave('route-leave');
          draftCache.set(fromKey, snapshotStateToEntry(get()));
          // The snapshot holds the BODY only, so the chrome the seller just edited would be lost on the
          // way out — the next page would compose the stale one. Hand it to `globalChrome` first: it is
          // the store-wide owner, and every page composes from it. This is what makes an UNSAVED header
          // edit follow the seller across pages instead of silently reverting.
          const chrome = serializeChrome(prev);
          if (chrome) adoptEditedChrome(chrome.header, chrome.footer);
        }

        // Seed the remembered preview product from storage on a COLD load (state is empty on a fresh reload)
        // so the canvas hydrates with the REAL product from the FIRST paint (no null→demo→real flash) and the
        // selector label shows it immediately. Object-set escape hatch (StorefrontProduct's readonly fields
        // don't fit an Immer WritableDraft slot — same reason setPreviewProduct uses `set({...})`). (0021)
        if (prev.previewProduct == null) {
          const snap = readPersistedPreviewProduct();
          if (snap) {
            set({ previewProduct: snap });
          }
        }

        // ASK FOR THE BOUND PRODUCT'S REVIEWS HERE (0066) — before the page load, in parallel with it.
        // The product id is already known (it came out of storage a line above, or it is still bound from
        // the page being left), so waiting costs the seller and buys nothing. It used to wait: the reviews
        // were fetched only by `bindPreviewProduct`, which on a cold boot runs after `load()` has resolved
        // AND after `revalidatePreviewProduct`'s own round trip — two sequential requests behind a canvas
        // that had already painted `product-reviews`'s "0 reviews" placeholder over a shop full of them.
        // Idempotent: `refreshPreviewReviews` no-ops on the demo (null), on a cached answer and on an id
        // already in flight, so the revalidation that follows finds the answer instead of re-fetching it.
        if (route.template === 'product') void refreshPreviewReviews(get().previewProduct);

        // ── DEV preset preview: open the builder at `?preset=electronics` to load the electronics theme
        // document + its theme settings (NON-persistent), so the preset can be explored and edited in the
        // real builder without seeding a store or hitting the backend. Index template only, DEV builds only.
        if (
          import.meta.env.DEV &&
          route.template === 'index' &&
          typeof location !== 'undefined' &&
          new URLSearchParams(location.search).get('preset') === 'electronics'
        ) {
          set((s) => {
            s.loadState = 'loading';
            s.route = route;
          });
          // The preset's own chrome, seeded WITHOUT a backend (this route has no store behind it).
          seedOfflineChrome(
            route.handle,
            route.locale,
            buildElectronicsHeaderDocument(localeOf(route)).sections,
            buildElectronicsFooterDocument(localeOf(route)).sections,
          );
          await applyLoadedDocument(
            set,
            route,
            {
              document: buildElectronicsHomeDocument(localeOf(route)),
              version: 0,
              publishedAt: null,
              hasUnpublishedChanges: false,
            },
            get().previewProduct,
            ELECTRONICS_THEME_SETTINGS,
            get().codConfig,
            get().storeCountry,
            get().storeCurrency,
            get().storeLanguage,
            get().previewOffer,
            get().storePaymentMethod,
          );
          // SUPPRESS the live 0019 channel for the whole preset session: the loaded settings are the
          // PRESET's, and any tweak would otherwise PUT them to the seller's LIVE row (live-on-save) —
          // this poisoned a real store's presetId once. Same staging flag as theme workspaces.
          set({ themeStagedForPublish: true });
          return;
        }

        // ── DEV preset preview: `?preset=electronics-product` opens the builder on a PRODUCT template with
        // a bound demo electronics product (real images + price), so the gallery + buy-box render fully
        // without a store/backend. Forces the product route (boot route is index). DEV builds only.
        if (
          import.meta.env.DEV &&
          typeof location !== 'undefined' &&
          new URLSearchParams(location.search).get('preset') === 'electronics-product'
        ) {
          const prodRoute = { ...route, template: 'product' as TemplateKey, handle: 'default' };
          set((s) => {
            s.loadState = 'loading';
            s.route = prodRoute;
          });
          seedOfflineChrome(
            prodRoute.handle,
            prodRoute.locale,
            buildElectronicsHeaderDocument(localeOf(prodRoute)).sections,
            buildElectronicsFooterDocument(localeOf(prodRoute)).sections,
          );
          await applyLoadedDocument(
            set,
            prodRoute,
            {
              document: buildElectronicsProductDocument(localeOf(prodRoute)),
              version: 0,
              publishedAt: null,
              hasUnpublishedChanges: false,
            },
            ELECTRONICS_DEMO_PRODUCT, // bound product → pageResourcesFor makes this a product route
            ELECTRONICS_THEME_SETTINGS,
            get().codConfig,
            get().storeCountry,
            get().storeCurrency,
            get().storeLanguage,
            get().previewOffer,
            get().storePaymentMethod,
          );
          // Bind the demo product into state so the canvas re-derives the context with it (the buy-box
          // resolves product data from get().previewProduct, not just the initial renderContext).
          // The demo is not a catalog product, so it carries no assignment — clear any stale one, else the
          // previously-bound product's template would be reported as this one's.
          set((s) => {
            s.previewProductTemplate = null;
            s.previewProductTemplateKnown = false;
          });
          get().setPreviewProduct(ELECTRONICS_DEMO_PRODUCT);
          // Same live-channel suppression as the index preset branch above (poison guard).
          set({ themeStagedForPublish: true });
          return;
        }

        // ── SEAMLESS PATH: switching to an already-cached page restores it INSTANTLY — no loading state, no
        // network on the critical path. The store theme is global and kept live in `themeSettings`, so reuse
        // it (never re-fetch on a switch). Guarded by themeEverLoaded so we never restore before a real load.
        // The store's global chrome must already be the one THIS route needs — the restore is synchronous
        // and cannot await a fetch. Crossing a theme-workspace boundary changes the chrome handle, so that
        // switch falls through to the fetch path below.
        const cached = draftCache.get(toKey);
        if (
          cached &&
          themeEverLoaded &&
          fromKey !== toKey &&
          chromeMatches(chromeHandleOf(route), route.locale)
        ) {
          restoreFromCache(
            set,
            route,
            cached,
            get().themeSettings,
            get().previewProduct,
            get().codConfig,
            get().storeCountry,
            get().storeCurrency,
            get().storeLanguage,
            get().previewOffer,
            get().storePaymentMethod,
          );
          // Re-fetch the preview offer on EVERY product-route load with a bound product (not only on a
          // storage-restore): a re-load / re-apply where the product is already live must still re-resolve the
          // offer, otherwise a transiently-dropped `previewOffer` never comes back and the tiles vanish for good.
          if (route.template === 'product' && get().previewProduct)
            void revalidatePreviewProduct(followProduct);
          await warmThemeWorkspacePayload(route);
          get().syncThemeWorkspace();
          carryPendingChromeEdit(set);
          return;
        }

        // ── FETCH PATH: first load or a page not yet cached — the smooth canvas overlay shows while it loads.
        set((s) => {
          s.loadState = 'loading';
          s.route = route;
        });
        // Drop any pending theme save before the (first) theme fetch so a stale flush can't clobber it.
        cancelThemeSave();
        // Fetch the draft, and the theme ONLY on the first load (it's store-global and kept live afterwards,
        // so a page switch never re-fetches it) — concurrently, so a first load is bounded by the slower
        // fetch, not their sum. loadStoreTheme never rejects (self-falls-back to the Shrine default). (0021)
        const themePromise = themeEverLoaded
          ? Promise.resolve(get().themeSettings)
          : loadStoreTheme();
        // Same one-shot fetch policy as the theme (feature 0026): COD config is store-global, fetched only on
        // the first load, kept live afterwards. Concurrent with the draft + theme; never rejects.
        const codConfigPromise = codConfigEverLoaded
          ? Promise.resolve(get().codConfig)
          : loadStoreCodConfig();
        try {
          const res = await builderApi.getDraft(route);
          const theme = await themePromise;
          const codConfig = await codConfigPromise;
          // First-ever theme fetch = the authoritative LIVE settings snapshot (workspace staging
          // re-themes `themeSettings` locally; this is what leaving a workspace restores).
          if (!themeEverLoaded) set({ liveThemeSettings: theme });
          themeEverLoaded = true;
          codConfigEverLoaded = true;
          // The store's ONE header/footer, loaded once per handle+locale and composed around every page
          // from here on. Needs the theme first (it supplies the seed for a store with no chrome row).
          await loadChromeFor(route, theme, get().storeLanguage);
          await applyLoadedDocument(
            set,
            route,
            {
              document: res.document,
              version: res.version,
              publishedAt: res.publishedAt,
              hasUnpublishedChanges: res.hasUnpublishedChanges,
            },
            get().previewProduct,
            theme,
            codConfig,
            get().storeCountry,
            get().storeCurrency,
            get().storeLanguage,
            get().previewOffer,
            get().storePaymentMethod,
          );
          draftCache.set(toKey, snapshotStateToEntry(get()));
          carryPendingChromeEdit(set);
        } catch (e) {
          // A genuinely MISSING draft (no backend / unseeded route) is NOT an error: open the builder to
          // the default "premium-calm" theme seed so it always shows a complete page (build-plan §M5 seed).
          // SessionExpiredError / NoStoreError still route to their dedicated full-screen states.
          if (isMissingDraftError(e)) {
            const theme = await themePromise;
            const codConfig = await codConfigPromise;
            themeEverLoaded = true;
            codConfigEverLoaded = true;
            await loadChromeFor(route, theme, get().storeLanguage);
            const seed = seedDraftFor(route);
            await applyLoadedDocument(
              set,
              route,
              {
                document: seed.document,
                version: seed.version,
                publishedAt: null,
                hasUnpublishedChanges: false,
              },
              get().previewProduct,
              theme,
              codConfig,
              get().storeCountry,
              get().storeCurrency,
              get().storeLanguage,
              get().previewOffer,
              get().storePaymentMethod,
            );
            draftCache.set(toKey, snapshotStateToEntry(get()));
            carryPendingChromeEdit(set);
            // Re-fetch the preview offer on EVERY product-route load with a bound product (not only on a
            // storage-restore): a re-load / re-apply where the product is already live must still re-resolve the
            // offer, otherwise a transiently-dropped `previewOffer` never comes back and the tiles vanish for good.
            if (route.template === 'product' && get().previewProduct)
              void revalidatePreviewProduct(followProduct);
            await warmThemeWorkspacePayload(route);
            get().syncThemeWorkspace();
            return;
          }
          set((s) => {
            if (e instanceof SessionExpiredError) s.loadState = 'session-expired';
            else if (e instanceof NoStoreError) s.loadState = 'no-store';
            else s.loadState = 'error';
          });
          throw e;
        }
        // Success: revalidate a storage-restored snapshot against the API (edits/deletions). Non-blocking —
        // the canvas already shows the cached snapshot, so this never delays first paint.
        // Re-fetch the preview offer on EVERY product-route load (see the two sibling call-sites): so a re-load
        // where the product is already live still re-resolves the offer and the tiles never vanish for good.
        if (route.template === 'product' && get().previewProduct)
          void revalidatePreviewProduct(followProduct);
        // Theme-workspace staging follows EVERY route change (spec 0032): load() is the single
        // navigation chokepoint (router, page switcher, applyTheme, publishTheme all funnel here), so
        // syncing here — on all three ready exits — guarantees no call-site can forget it.
        await warmThemeWorkspacePayload(route);
        get().syncThemeWorkspace();
      },

      async prefetchRoute(route) {
        // Warm the page cache (feature 0021) so the FIRST switch to a page is instant too. Best-effort, off
        // the critical path: skip if already cached / in flight, or before the first real load (no theme yet).
        const key = routeKeyOf(route);
        if (draftCache.has(key) || prefetchInFlight.has(key) || get().loadState !== 'ready') return;
        prefetchInFlight.add(key);
        try {
          const res = await builderApi.getDraft(route);
          draftCache.set(
            key,
            entryFromDraft({
              document: res.document,
              version: res.version,
              publishedAt: res.publishedAt,
              hasUnpublishedChanges: res.hasUnpublishedChanges,
            }),
          );
        } catch (e) {
          // An unseeded route prefetches to its default seed (so its first open is instant too); other
          // failures are swallowed (the real load() will surface them if the seller actually navigates).
          if (isMissingDraftError(e)) {
            const seed = seedDraftFor(route);
            draftCache.set(
              key,
              entryFromDraft({
                document: seed.document,
                version: seed.version,
                publishedAt: null,
                hasUnpublishedChanges: false,
              }),
            );
          }
        } finally {
          prefetchInFlight.delete(key);
        }
      },

      async applyTheme(themeId, route = get().route, opts) {
        const def = await loadThemeDefinition(themeId);
        // Guard: unknown id, a coming-soon teaser, or a registry entry without an applyable payload →
        // ignore the param entirely (the URL may be stale or hand-edited) and boot normally.
        //
        // ALWAYS SAY SO (2026-08-20): booting normally lands on the LIVE pages, so a theme the registry
        // does not know about opened as "the house theme" with nothing on screen to explain it — which is
        // exactly how a stale ui-react pre-bundle (Folio missing from the dev server's optimized deps)
        // read as "the builder always opens Atlas". The console.warn is for us; the toast is what makes
        // the seller — and the next person debugging this — see that the theme was never applied.
        if (!def || def.status !== 'available' || !def.settings || !def.documents) {
          console.warn(
            `[builder] applyTheme: theme "${themeId}" is unknown or not applyable — ignored.`,
          );
          pushToast(
            bt(
              'chrome.theme.open_failed',
              'Couldn’t open that theme — it isn’t available. Your store is unchanged.',
            ),
            'error',
          );
          await get()
            .load(route)
            .catch(() => undefined);
          return;
        }
        // TWO DIFFERENT LOCALES, and conflating them published themes into rows no storefront reads
        // (2026-08-20, store `storetest` = Italian): every seeded row landed in `fr`, the live `it` pages
        // never changed, and the seller saw her old default home — "j'ai publié un thème et il n'y a plus
        // d'images". `localeOf` answers "which language is this COPY written in" — since 2026-09-13 the
        // store's own language, served by the theme's dictionary (see the loop below).
        // The ROW KEY must stay the route's own locale — absent means "the STORE's own language", which
        // the backend resolves (0050 lot 0, same rule builderApi already documents for its query).
        const seedLocale = localeOf(route, get().storeLanguage);
        const rowLocale = route.locale;
        const wsHandle = themeWorkspaceHandle(themeId);
        let reopened = false;
        // Au moins un gabarit rouvert était SANS estampille : on ne peut pas prouver que
        // c'est le travail du vendeur, donc on ne l'écrase pas — mais on le signale.
        let reopenedStale = false;
        try {
          // ONE DRAFT PER THEME (workspace model): seed the theme's templates into ITS OWN workspace
          // handle through the SAME draft-save path the editor uses — but NEVER overwrite: a template
          // whose workspace draft already exists is the seller's earlier customization of this theme,
          // so applying again REOPENS it untouched. The live handle 'default' is never written here,
          // and `builderApi.publish` is NEVER called — the live store only changes on publishTheme().
          //
          // HEADER/FOOTER (superseded 2026-08-16 — the founder's "one header for the whole store").
          // The 2026-07-23 decision here was to seed every theme document VERBATIM, chrome included,
          // because the storefront de-duplicated and the builder had no global-region composition. That
          // is exactly what let the home and the product page drift onto two different headers.
          // Now: theme documents are CHROME-FREE, and the theme ships its chrome as two more entries in
          // `def.documents` ('header' / 'footer'). They flow through this same loop, so applying a theme
          // seeds the store's chrome ONCE — into the workspace, published with the theme — instead of
          // stamping a private copy into each template. Templates the theme does not cover keep the
          // store's existing chrome, restyled live by the staged ThemeSettings.
          for (const [template, build] of Object.entries(def.documents)) {
            if (!build) continue;
            const wsRoute: BuilderRoute = { template, handle: wsHandle, locale: rowLocale };
            // THE SEED SPEAKS THE STORE'S LANGUAGE (2026-09-13). A theme is typed once (French, or
            // English for Electronics); its dictionary for `seedLocale` is written INTO the seed here,
            // before the stamp — so the signature fingerprints the German text, the draft the seller
            // opens is German, and nothing is left to resolve at render time. A language without a
            // dictionary keeps the author's text, exactly as before.
            const document: PageDocument = stampThemeSeed({
              ...(await localizeThemeDocument(themeId, build(seedLocale), seedLocale)),
              template: template as TemplateKey,
              handle: wsHandle,
              // The document's own field describes the language its TEXT is in.
              locale: seedLocale,
            });
            let existingVersion = 0; // 404 below → first save at version 0
            try {
              const existing = await builderApi.getDraft(wsRoute);
              // A workspace draft exists. Two very different cases, and conflating them is what froze
              // stores on a Volt three revisions old: a draft the seller EDITED is theirs and is
              // reopened untouched, but a PRISTINE seed is just a stale copy of a document we own, so
              // it follows the theme forward. `isPristineThemeSeed` can only answer yes when the
              // document still matches its own seed stamp — no edit can be mistaken for staleness.
              // `reset` (URL `?resetTheme=1`) est l'ÉCHAPPATOIRE, et elle existe pour un trou réel :
              // un brouillon SANS estampille — semé avant que l'estampille existe — ne peut être
              // reconnu ni comme seed intact ni comme travail du vendeur, donc `isPristineThemeSeed`
              // répond non et le workspace est figé POUR TOUJOURS sur une version du thème d'il y a
              // des semaines. `knownPastSeeds` était prévu pour ça et n'a jamais été transmis :
              // maintenir l'empreinte de chaque révision jamais livrée n'est pas tenable. Le vendeur
              // reprend donc la main explicitement, et on le lui DIT (toast plus bas) au lieu de
              // rouvrir l'ancienne page sans un mot — c'est ce silence qui a fait dire « le builder
              // me donne l'ancien thème ».
              const stale = !existing.document.meta?.themeSeed;
              if (
                !opts?.reset &&
                (!isPristineThemeSeed(existing.document) ||
                  existing.document.meta?.themeSeed === document.meta?.themeSeed)
              ) {
                reopened = true;
                reopenedStale = reopenedStale || stale;
                continue;
              }
              existingVersion = existing.version;
            } catch (e) {
              if (!isMissingDraftError(e)) throw e; // 404 = no workspace draft yet → seed below
            }
            await builderApi.saveDraft(wsRoute, document, existingVersion);
            // The just-seeded draft supersedes any in-session cache entry — drop it so the next open
            // (including the load below) fetches the fresh server copy instead of a stale snapshot.
            draftCache.delete(routeKeyOf(wsRoute));
          }
          // Same reason, for the chrome: the theme just seeded the workspace's header/footer rows, so the
          // session's in-memory chrome is stale. Drop it and let the load below fetch the theme's chrome.
          resetGlobalChrome();
        } catch (e) {
          // Seeding failed (offline / version race / no store): surface it through the existing toast
          // channel and boot normally — an apply must never blank the editor.
          console.warn('[builder] applyTheme: seeding failed — booting without applying.', e);
          pushToast(
            btp(
              'chrome.theme.apply_failed',
              'Couldn’t apply {name} — your store is unchanged. Please try again.',
              { name: def.name },
            ),
            'error',
          );
          await get()
            .load(route)
            .catch(() => undefined);
          return;
        }
        // Open the workspace's index draft through the normal load path; `syncThemeWorkspace` (called
        // below, and by the router after every subsequent load) stages the theme's settings locally —
        // the 0019 channel is live-on-save and the public storefront reads that row, so workspace
        // settings must NEVER be PUT before publishTheme().
        await get()
          .load({ template: 'index', handle: wsHandle, locale: rowLocale })
          .catch(() => undefined);
        if (get().loadState !== 'ready') return;
        if (reopenedStale) {
          // Le cas qui rendait fou : la page à l'écran est une VIEILLE copie du thème, et le message
          // de succès d'avant (« Resumed your draft ») laissait croire à un bug du thème.
          // On ne SAIT pas si ce brouillon est une customisation du vendeur ou une vieille copie du
          // thème — c'est tout le problème d'un brouillon sans estampille. Le message dit donc
          // l'incertitude ET le remède, en annonçant que le remède REMPLACE le brouillon.
          pushToast(
            btp(
              'chrome.theme.reopened_stale',
              'Reopened your existing {name} draft. It was created before we started tracking theme versions, so it may not include the latest {name}. Add "&resetTheme=1" to the URL to start again from the current theme — this replaces the draft.',
              { name: def.name },
            ),
            'info',
          );
        } else {
          pushToast(
            reopened
              ? btp(
                  'chrome.theme.resumed',
                  'Resumed your {name} draft — pick up where you left off.',
                  { name: def.name },
                )
              : btp(
                  'chrome.theme.staged',
                  '{name} is set up as a draft — your live store is unchanged until you publish.',
                  { name: def.name },
                ),
            'success',
          );
        }
      },

      syncThemeWorkspace() {
        const { route, loadState } = get();
        if (loadState !== 'ready') return;
        if (isThemeWorkspaceHandle(route.handle)) {
          // Entering (or refreshing inside) a theme workspace: stage the seller's persisted settings
          // for THIS theme, falling back to the registry defaults on first entry. The flag suppresses
          // saveTheme's live channel; the canvas re-themes from local state only.
          const themeId = themeIdOfWorkspaceHandle(route.handle);
          // PEEK, not load: this pass is synchronous by contract (see below), and `load()` has already
          // awaited the theme's chunk via `warmThemeWorkspacePayload` on every path that reaches here.
          const def = peekThemePayload(themeId);
          // Bind the THEME's own demo product (Volt → Aurio Open Pro): the workspace is the theme's
          // SHOWROOM, so ENTERING it always previews on-brand content — even over the seller's own
          // preview product, which is remembered and restored on exit (founder 2026-07-24). Only the
          // ENTRY transition rebinds: a product the seller picks while INSIDE the workspace survives
          // subsequent in-workspace loads. EPHEMERAL (persist=false): never written to the seller's
          // persisted preview slot. Bound BEFORE the settings staging below so updateThemeSettings
          // rebuilds the context with both.
          {
            const entering = !get().themeStagedForPublish;
            const demo = def?.demoProduct;
            const cur = get().previewProduct;
            const curIsEphemeralDemo =
              workspaceDemoProductId !== null && cur?.id === workspaceDemoProductId;
            if (demo && (entering || cur == null || (curIsEphemeralDemo && cur.id !== demo.id))) {
              // Remember the seller's OWN preview once per entry (never a lingering ephemeral demo).
              if (entering && !curIsEphemeralDemo) sellerPreviewBeforeWorkspace = cur;
              workspaceDemoProductId = demo.id;
              if (cur?.id !== demo.id) bindPreviewProduct(demo, false);
            } else if (!demo && curIsEphemeralDemo) {
              workspaceDemoProductId = null;
              bindPreviewProduct(sellerPreviewBeforeWorkspace, false);
              sellerPreviewBeforeWorkspace = null;
            }
          }
          // Staged settings are read SYNCHRONOUSLY from the workspace cache (this function runs after
          // every load and cannot await). A miss falls back to the registry defaults exactly as before,
          // and `hydrateStagedTheme` below applies the server's copy when it lands — but ONLY if the
          // seller has not edited in the meantime, so a slow fetch can never clobber a live edit.
          const staged = readStagedTheme(themeId) ?? def?.settings;
          set({ themeStagedForPublish: true });
          if (staged) get().updateThemeSettings({ ...staged });
          if (!isStagedThemeHydrated(themeId)) {
            const applied = JSON.stringify(get().themeSettings);
            void hydrateStagedTheme(themeId).then((server) => {
              if (!server) return;
              const st = get();
              // Still in THIS workspace, and untouched since we staged the fallback.
              if (!isThemeWorkspaceHandle(st.route.handle)) return;
              if (themeIdOfWorkspaceHandle(st.route.handle) !== themeId) return;
              if (JSON.stringify(st.themeSettings) !== applied) return;
              st.updateThemeSettings({ ...server });
            });
          }
        } else {
          // On ANY non-workspace route: drop a still-bound ephemeral theme demo, restoring the
          // seller's OWN preview remembered at entry. Checked OUTSIDE the themeStagedForPublish
          // branch because publishTheme() clears that flag itself before its final load — the demo
          // must still be dropped on that exit path. A product the seller explicitly picked inside
          // the workspace has a different id → kept (it persisted normally).
          if (workspaceDemoProductId !== null) {
            if (get().previewProduct?.id === workspaceDemoProductId)
              bindPreviewProduct(sellerPreviewBeforeWorkspace, false);
            workspaceDemoProductId = null;
            sellerPreviewBeforeWorkspace = null;
          }
          if (get().themeStagedForPublish) {
            // Leaving a workspace for a live route: restore the LIVE theme (load() only fetches it on
            // the very first boot) and drop the suppressed pending save — workspace settings must not
            // leak to the live row on navigation.
            get().updateThemeSettings({ ...get().liveThemeSettings });
            set({ themeStagedForPublish: false });
            cancelThemeSave();
          }
        }
      },

      async publishTheme() {
        const route = get().route;
        if (!isThemeWorkspaceHandle(route.handle)) return false;
        const themeId = themeIdOfWorkspaceHandle(route.handle);
        const def = await loadThemeDefinition(themeId);
        if (!def?.documents) return false;
        // The ROW KEY, never `localeOf` (see applyTheme): collapsing the store's language to `fr` here
        // copied the workspace over `fr` live rows and published those, while the storefront kept serving
        // the store's own locale — a publish that reported success and changed nothing the customer sees.
        const locale = route.locale;
        const wsHandle = themeWorkspaceHandle(themeId);
        const stagedSettings = { ...get().themeSettings };
        // Publish EVERY template that has a workspace draft — the theme's own set PLUS any template
        // the seller opened (and thereby seeded) inside the workspace. Missing candidates just skip.
        const candidates = [
          ...new Set([
            ...Object.keys(def.documents),
            'index',
            'product',
            'collection',
            'collections',
            'cart',
            'thank-you',
            'checkout',
            'search',
            '404',
            'header',
            'footer',
          ]),
        ];
        try {
          // Copy every workspace draft over the LIVE handle and publish it — the only moment the
          // seller's live pages change. Version-aware on the live side (read → save → publish).
          for (const template of candidates) {
            const wsRoute: BuilderRoute = { template, handle: wsHandle, locale };
            let wsDraft;
            try {
              wsDraft = await builderApi.getDraft(wsRoute);
            } catch (e) {
              if (isMissingDraftError(e)) continue; // template never seeded in this workspace
              throw e;
            }
            const liveRoute: BuilderRoute = { template, handle: 'default', locale };
            let liveVersion = 0;
            try {
              liveVersion = (await builderApi.getDraft(liveRoute)).version;
            } catch (e) {
              if (!isMissingDraftError(e)) throw e;
            }
            const document = { ...wsDraft.document, handle: 'default' } as PageDocument;
            await builderApi.saveDraft(liveRoute, document, liveVersion);
            await builderApi.publish(liveRoute);
            draftCache.delete(routeKeyOf(liveRoute));
          }
          // Pages are live → release the staged settings NOW and flush them SYNCHRONOUSLY (the flush
          // reads the store at fire time, so it must run before the load below re-resolves state).
          set({ themeStagedForPublish: false, liveThemeSettings: stagedSettings });
          persistStagedTheme(themeId, null);
          scheduleThemeSave();
          await flushThemeSave();
          pushToast(
            btp('chrome.theme.live', '{name} is now live — this is the theme your customers see.', {
              name: def.name,
            }),
            'success',
          );
        } catch (e) {
          console.warn('[builder] publishTheme failed — the workspace is untouched.', e);
          pushToast(
            btp(
              'chrome.theme.publish_failed',
              'Couldn’t publish {name} — your live store is unchanged. Please try again.',
              { name: def.name },
            ),
            'error',
          );
          return false;
        }
        // The workspace BECAME the live theme (Shopify model) — CONSUME it so there is exactly ONE
        // copy of the theme from here on: "Customize" edits the live one, and a later "Use this
        // theme" re-seeds a FRESH workspace from the (possibly updated) registry definition instead
        // of resuming a stale draft. Best-effort: a failed delete only means the next apply says
        // "Resumed your draft" once more — never fail a publish that already succeeded.
        for (const template of candidates) {
          await builderApi
            .deleteWorkspacePage({ template, handle: wsHandle, locale })
            .catch(() => undefined);
          draftCache.delete(routeKeyOf({ template, handle: wsHandle, locale }));
        }
        // Move the seller onto the LIVE copy — the one Volt that now exists.
        await get()
          .load({ template: 'index', handle: 'default', locale })
          .catch(() => undefined);
        return true;
      },

      async listTemplateHandles() {
        const { template, locale } = get().route;
        try {
          // Theme workspaces are internal storage, not seller-facing named templates — hide them.
          return (await builderApi.listTemplates(template, locale)).handles.filter(
            (h) => !isThemeWorkspaceHandle(h),
          );
        } catch {
          // Discovery is best-effort: a failure (offline / no store) yields no extra handles; the
          // selector still shows the current handle + 'default'.
          return [];
        }
      },

      async listTemplateEntries() {
        const { template, locale } = get().route;
        try {
          const res = await builderApi.listTemplates(template, locale);
          // An older backend returns only `handles`; treat those as published (they came from the
          // published-only query), so the selector degrades to today's behaviour instead of blanking.
          const entries: TemplateEntry[] =
            res.templates?.map((t) => ({
              handle: t.handle,
              status: t.status,
              hasUnpublishedChanges: t.hasUnpublishedChanges,
            })) ??
            res.handles.map((h) => ({
              handle: h,
              status: 'PUBLISHED' as const,
              hasUnpublishedChanges: false,
            }));
          return entries.filter((e) => !isThemeWorkspaceHandle(e.handle));
        } catch {
          return null; // NOT `[]` — see the doc on listTemplateEntries: an empty list is a claim, not a failure.
        }
      },

      async loadTemplateUsage() {
        // Store-wide counts, refetched on demand (an assignment changes them). A failed fetch resolves to
        // null and is DROPPED rather than stored: overwriting good counts (or a clean `null`) with an empty
        // map would render "Aucun produit" on every template — a lie the seller would act on.
        //
        // ONLY THE LATEST ANSWER WINS (26/10/2026). Creating a template fires one of these (the handle
        // changed), and the assignment that follows fires another; the first was still in flight, and its
        // pre-assignment counts landed last — so the switcher read "No products" about the layout the
        // product had just been put on. An older request's answer is no longer about the store as it is.
        const ticket = ++templateUsageTicket;
        const counts = await catalogApi.templateUsage();
        if (counts === null || ticket !== templateUsageTicket) return;
        set((s) => {
          s.templateUsage = counts;
        });
      },

      async assignTemplateToPreviewProduct(handle) {
        // Bind the PREVIEWED product to the template being edited — the action that closes the loop
        // between "I designed this layout" and "a real product renders it". Only meaningful on a product
        // route with a real product bound; the caller (Topbar) gates the button on both.
        const product = get().previewProduct;
        if (!product) throw new Error('No preview product bound');
        await get().assignTemplateToProduct(product.id, handle);
      },

      async assignTemplateToProduct(productId, handle) {
        await catalogApi.assignTemplate(productId, handle);
        set((s) => {
          // Only the PREVIEWED product's template is mirrored in state. Assigning some other product
          // must not repaint "Uses <handle>" under the product on the canvas — that line would then
          // name a template this product does not render.
          if (s.previewProduct?.id === productId) {
            s.previewProductTemplate = handle;
            s.previewProductTemplateKnown = true;
          }
        });
        // Refresh the counts so the "N products" line reflects the assignment immediately.
        await get().loadTemplateUsage();
      },

      async selectTemplate(handle) {
        await get().load({ ...get().route, handle });
      },

      async createTemplate(handle, source = 'default') {
        // Superadmin consultation: refuse BEFORE touching local state. The save below is blocked by the
        // http lock anyway, but by then `applyLoadedDocument` has already switched the builder onto the
        // new handle — which the selector then lists as a "Brouillon", a template that never existed
        // server-side (seen by the founder 06/09: phantom "test3432342").
        if (isReadOnlyView()) throw new ReadOnlyViewError();
        const open = get().route;
        await leaveOpenPage(get);
        const route = { ...open, handle };
        // A template is always born as a COPY of one the seller can point at (founder, 19/09/2026): the
        // "blank from the theme's seed" start is gone, because what it produced looked like no page she
        // had ever seen. `source` is another handle of this type — `default` is the base page.
        //
        // Copying the handle that is OPEN takes the document on screen, unsaved edits included (the same
        // rule `createPage` follows). Any other source is read from its saved draft; a source with no row
        // yet — a template created a moment ago and never saved — falls back to the type's seed rather
        // than failing the create. `reidDocument` gives the copy its own section/block ids, without which
        // an edit to the child would rewrite the parent's sections (see childTemplateIsolation.test.ts).
        //
        // Persisted IMMEDIATELY (version 0 → INSERT): a created-then-walked-away template left at
        // dirty:false would never write a row (autosave early-returns on !dirty), so we save DIRECTLY
        // here rather than rely on the debounced autosave (0018 M-F2).
        const fromOpen = get().loadState === 'ready' && source === open.handle;
        const base = fromOpen
          ? serialize(get())
          : await templateSourceDocument({ ...open, handle: source });
        const document: PageDocument = reidDocument({ ...base, handle });
        await applyLoadedDocument(
          set,
          route,
          {
            document,
            version: 0,
            publishedAt: null,
            // TRUE, and it must be: a template that has NEVER been published has, by definition, something
            // to publish. Hardcoding false here dead-ended the seller — Publish stayed disabled ("all
            // changes saved"), so the template could never leave DRAFT, and assignment is PUBLISHED-only,
            // so it could never reach a product either. The only escape was to make a throwaway edit.
            // This mirrors what the server itself answers on the next load (StorePageService: a draft with
            // no published row IS ahead of it) — the two disagreed only in the moments right after create.
            hasUnpublishedChanges: true,
          },
          get().previewProduct,
          // Creating a named template doesn't touch the store's global theme / COD config — carry them.
          get().themeSettings,
          get().codConfig,
          get().storeCountry,
          get().storeCurrency,
          get().storeLanguage,
          get().previewOffer,
          get().storePaymentMethod,
        );
        const res = await builderApi.saveDraft(route, serialize(get()), 0);
        set((s) => {
          s.serverVersion = res.version;
          s.dirty = false;
          s.saveState = 'saved';
          s.lastSavedAt = Date.now();
        });
        await publishNewTemplate(set, route);
      },

      async listPages() {
        const { locale } = get().route;
        try {
          return (await builderApi.listPages(locale)).pages;
        } catch {
          // A failed lookup still yields no pages — but "you have none" and "we couldn't ask" look
          // IDENTICAL in the submenu, and a seller reads that empty list as "my page wasn't created".
          // Say which one it is.
          pushToast(
            bt('chrome.pages.load_failed', 'Couldn’t load your pages. Please try again.'),
            'error',
          );
          return [];
        }
      },

      async createPage(title, source) {
        const { locale } = get().route;
        // The starter document is generated CLIENT-SIDE (the blank-page theme, or a copy of `source` — see
        // starterDocumentFrom) and sent to the server, which owns the slug + INSERT and returns the
        // generated handle — the builder then opens it (load fetches the just-persisted draft). Unlike
        // createTemplate we do NOT pre-key the route: the handle is the server's to mint.
        const open = get().route;
        // Only a READY editor holds a real document — an empty/loading store would serialize to zero
        // sections and create a blank page while claiming it copied one.
        const isOpenPage =
          source != null &&
          get().loadState === 'ready' &&
          source.template === open.template &&
          source.handle === open.handle;
        const document = source
          ? await starterDocumentFrom(source, locale, isOpenPage ? serialize(get()) : null)
          : buildDefaultDocument('page', localeOf({ ...get().route, template: 'page' }));
        const res = await builderApi.createPage(locale, title, document);
        await get().load({ template: 'page', handle: res.handle, locale });
        return res.handle;
      },

      async renamePage(handle, title) {
        await builderApi.renamePage(get().route.locale, handle, title);
      },

      async deletePage(handle) {
        const { locale, template, handle: current } = get().route;
        await builderApi.deletePage(locale, handle);
        // If the seller deleted the page they were editing, fall back to the home page.
        if (template === 'page' && current === handle) {
          await get().load({ template: 'index', handle: 'default', locale });
        }
      },

      setPreviewProduct(product) {
        // Bind a REAL product into the product-template preview (feature 0018 M2). This is NOT an editing
        // operation — it only swaps the canvas's render context (no dirty / no history), so the chosen
        // product re-resolves immediately on every section without re-loading the document. On non-product
        // routes the binding is inert (pageResourcesFor falls back to the 'home' demo). The context is
        // computed from the COMMITTED route (not an Immer draft) and applied via the object-merge form of
        // `set` — StorefrontProduct's readonly fields don't fit Immer's WritableDraft slots.
        // The choice is remembered (FULL snapshot, store-scoped, persist=true) so a reload restores THIS
        // product with no flash instead of falling back to the demo. Body shared with the workspace's
        // ephemeral demo binding — see bindPreviewProduct.
        bindPreviewProduct(product, true);
      },

      mergeResolvedProducts(products) {
        // Feature 0023 in the EDITOR: the storefront resolves picker refs server-side; the canvas resolves
        // them client-side (usePickedProducts) and folds them in here. Applied with the object form of `set`
        // — StorefrontProduct's readonly fields don't fit an Immer draft, same as setPreviewProduct. The
        // identity guard is what stops the caller's re-apply-after-rebuild effect from looping forever.
        const ctx = get().renderContext;
        if (!ctx) return;
        const current = ctx.resolved.products ?? {};
        const entries = Object.entries(products);
        if (entries.length === 0 || entries.every(([id, p]) => current[id] === p)) return;
        set({
          renderContext: {
            ...ctx,
            resolved: { ...ctx.resolved, products: { ...current, ...products } },
          },
        });
      },

      dropResolvedProducts(ids) {
        // Un-merge, same object-form `set` + identity guard as mergeResolvedProducts above (and the same
        // reason: the caller's re-apply-after-rebuild effect must terminate).
        const ctx = get().renderContext;
        if (!ctx) return;
        const current = ctx.resolved.products ?? {};
        if (!ids.some((id) => id in current)) return;
        const next: Record<string, StorefrontProduct> = {};
        for (const [id, product] of Object.entries(current)) {
          if (!ids.includes(id)) next[id] = product;
        }
        set({ renderContext: { ...ctx, resolved: { ...ctx.resolved, products: next } } });
      },

      selectCountdownSurface(target) {
        set((s) => {
          s.selectedCountdown = target;
          if (target) {
            s.selectedSectionId = null;
            s.selectedBlockId = null;
            s.focusedFieldId = null;
          }
        });
      },

      patchCountdownDesign(campaignId, design) {
        // Patch every payload that carries THIS campaign — a bar and a popup are two campaigns today,
        // but the banner rides the render context and the same id could serve more than one surface
        // tomorrow. Written as a sweep so it stays true either way.
        const patch = (raw: unknown): unknown => {
          if (!raw || typeof raw !== 'object') return raw;
          const payload = raw as Record<string, unknown>;
          if (payload.campaignId !== campaignId) return raw;
          const current = (payload.design ?? {}) as Record<string, unknown>;
          return { ...payload, design: { ...current, ...design } };
        };
        const nextBar = patch(get().countdownBar);
        const nextPopup = patch(get().countdownPopup);
        if (nextBar === get().countdownBar && nextPopup === get().countdownPopup) return;
        set({ countdownBar: nextBar, countdownPopup: nextPopup });
      },

      applyCountdownPreview(preview) {
        // The bar/popup are plain state — compared by value-ish identity so a poll that answers the same
        // thing twice does not re-render the canvas. `parseProductCountdown` / `parseCountdownBadges` are
        // the STOREFRONT's own parsers: the editor must read the wire exactly as the shop does, or the two
        // disagree about what a malformed campaign means.
        const ctx = get().renderContext;
        const banner = parseProductCountdown(preview.banner);
        const badges = parseCountdownBadges(preview.badges);
        const barChanged = get().countdownBar !== preview.bar;
        const popupChanged = get().countdownPopup !== preview.popup;
        const ctxChanged =
          ctx !== null &&
          (ctx.countdown !== banner || ctx.countdownBadges !== badges) &&
          // An untouched page answers `undefined` twice; comparing the PARSED values would then rebuild the
          // context on every poll (a fresh object each time) and re-render every section once a minute.
          !(
            ctx.countdown === undefined &&
            banner === undefined &&
            !hasBadges(badges) &&
            !hasBadges(ctx.countdownBadges)
          );
        if (!barChanged && !popupChanged && !ctxChanged) return;
        set({
          ...(barChanged ? { countdownBar: preview.bar } : null),
          ...(popupChanged ? { countdownPopup: preview.popup } : null),
          ...(ctxChanged && ctx
            ? { renderContext: { ...ctx, countdown: banner, countdownBadges: badges } }
            : null),
        });
      },

      mergeResolvedMenus(menus) {
        // Same shape as mergeResolvedProducts: MenuEntry is deeply readonly (it doesn't fit an Immer draft),
        // so this uses the object form of `set`, and the by-reference guard stops the self-healing re-apply
        // effect from looping after a context rebuild.
        const ctx = get().renderContext;
        if (!ctx) return;
        const current = ctx.resolved.menus ?? {};
        const entries = Object.entries(menus);
        if (entries.length === 0 || entries.every(([handle, m]) => current[handle] === m)) return;
        set({
          renderContext: {
            ...ctx,
            resolved: { ...ctx.resolved, menus: { ...current, ...menus } },
          },
        });
      },

      mergeResolvedCollections(collections) {
        // Editor half of feature 0023's collection refs, mirroring mergeResolvedProducts: object form of
        // `set` (StorefrontCollection is readonly and doesn't fit an Immer draft) + a by-reference guard.
        const ctx = get().renderContext;
        if (!ctx) return;
        const current = ctx.resolved.collections ?? {};
        const entries = Object.entries(collections);
        if (entries.length === 0 || entries.every(([id, c]) => current[id] === c)) return;
        set({
          renderContext: {
            ...ctx,
            resolved: { ...ctx.resolved, collections: { ...current, ...collections } },
          },
        });
      },

      mergeResolvedCollectionProducts(productsByCollection) {
        // Same object-form `set` + by-reference guard as mergeResolvedCollections. The guard compares the
        // ARRAY identity, so re-applying the same cached list after a context rebuild settles in one pass.
        const ctx = get().renderContext;
        if (!ctx) return;
        const current = ctx.resolved.collectionProducts ?? {};
        const entries = Object.entries(productsByCollection);
        if (entries.length === 0 || entries.every(([id, list]) => current[id] === list)) return;
        set({
          renderContext: {
            ...ctx,
            resolved: {
              ...ctx.resolved,
              collectionProducts: { ...current, ...productsByCollection },
            },
          },
        });
      },

      setStoreCollections(collections) {
        // Whole-list REPLACE, not a merge: this mirrors one server-side read (`getPublishedCollections`),
        // so a collection that left the store must leave the canvas too. The reference guard is what makes
        // the caller's re-apply-after-context-rebuild effect terminate.
        const ctx = get().renderContext;
        if (!ctx) return;
        if (ctx.resolved.storeCollections === collections) return;
        set({
          renderContext: {
            ...ctx,
            resolved: { ...ctx.resolved, storeCollections: collections },
          },
        });
      },

      async loadCatalog() {
        // Fetch the product list ONCE into the shared cache (feature 0021). Idempotent so it's safe to call
        // both on idle-prefetch and on menu-open; a failure is best-effort (the picker shows a retry hint).
        const st = get();
        if (st.catalogState === 'loading' || st.catalogState === 'loaded') return;
        set((s) => {
          s.catalogState = 'loading';
        });
        try {
          const items = await catalogApi.listProducts();
          set((s) => {
            s.catalog = items;
            s.catalogState = 'loaded';
          });
        } catch {
          set((s) => {
            s.catalogState = 'error';
          });
        }
      },

      async loadCollectionsCatalog() {
        const st = get();
        if (st.collectionsCatalogState === 'loading' || st.collectionsCatalogState === 'loaded')
          return;
        set((s) => {
          s.collectionsCatalogState = 'loading';
        });
        try {
          const items = await listStoreCollections();
          set({ collectionsCatalog: items, collectionsCatalogState: 'loaded' });
        } catch {
          set((s) => {
            s.collectionsCatalogState = 'error';
          });
        }
      },

      async bindPreviewCollection(id, opts) {
        const repaint = opts?.repaint !== false;
        // `repaint: false` = called by `applyLoadedDocument` BEFORE the first paint: the document about to be
        // applied builds its own context from the binding, so rebuilding the OLD one here would only flash it.
        const paint = (): { renderContext?: RenderContext } =>
          repaint ? { renderContext: rebuild() } : {};
        // Rebuild the canvas context from the CURRENT store facts — the same call `bindPreviewProduct`
        // makes, for the same reason: `buildEditContext` defaults the theme / COD config when not passed.
        const rebuild = (): RenderContext =>
          buildEditContext(
            localeOf(get().route, get().storeLanguage),
            pageResourcesFor(get().route, get().previewProduct),
            get().themeSettings,
            get().codConfig,
            get().storeCountry,
            get().storeCurrency,
            get().previewOffer ?? undefined,
            get().storePaymentMethod ?? undefined,
          );
        if (id === null) {
          boundCollection = null;
          persistPreviewCollectionId(null);
          set({ previewCollection: null, previewCollectionState: 'loaded', ...paint() });
          return;
        }
        set((s) => {
          s.previewCollectionState = 'loading';
        });
        try {
          // A collection handed over from the store list needs no second read — only its products.
          const [collection, products] = await Promise.all([
            opts?.preloaded ? Promise.resolve(opts.preloaded) : getStorefrontCollection(id),
            getCollectionStorefrontProducts(id),
          ]);
          if (!collection) {
            // Archived / deleted since it was remembered: forget it rather than paint a ghost.
            boundCollection = null;
            persistPreviewCollectionId(null);
            set({ previewCollection: null, previewCollectionState: 'loaded', ...paint() });
            return;
          }
          boundCollection = { collection, products };
          persistPreviewCollectionId(id);
          set({ previewCollection: collection, previewCollectionState: 'loaded', ...paint() });
        } catch {
          set((s) => {
            s.previewCollectionState = 'error';
          });
          throw new Error('preview-collection-failed');
        }
      },

      async ensurePreviewCollection(forRoute, opts) {
        // The route being LOADED, not the one in state: `load()` sets `route` at different moments on its
        // four paths, and a switch from a product page must bind a collection into the page that is coming.
        const route = forRoute ?? get().route;
        if (route.template !== 'collection') return;
        if (boundCollection) {
          // Bound on an earlier visit: the context built by `load()` already carries it (pageResourcesFor
          // reads the module binding); only the selector label may lag behind.
          if (get().previewCollection?.id !== boundCollection.collection.id) {
            set({
              previewCollection: boundCollection.collection,
              previewCollectionState: 'loaded',
            });
          }
          return;
        }
        const remembered = readPersistedPreviewCollectionId();
        if (remembered) {
          try {
            await get().bindPreviewCollection(remembered, { repaint: opts?.repaint });
          } catch {
            /* fall through to the first collection */
          }
          if (boundCollection) return;
        }
        await get().loadCollectionsCatalog();
        const first = get().collectionsCatalog[0];
        if (!first || boundCollection) return;
        try {
          // The list row IS the collection (same DTO fields) — hand it over, fetch only its products.
          await get().bindPreviewCollection(first.id, { preloaded: first, repaint: opts?.repaint });
        } catch {
          /* best-effort: the demo collection stays, the selector shows the error state */
        }
      },

      async loadStore() {
        // Fetch the store slug ONCE so the "Preview" button can build the public storefront URL (the JWT
        // only carries the opaque store_id). Idempotent + best-effort — a failure disables the button.
        const st = get();
        if (st.storeState === 'loading' || st.storeState === 'loaded') return;
        set((s) => {
          s.storeState = 'loading';
        });
        // The store's ONE payment method rides along so the buy-box authoring surfaces stay honest. Fetched
        // CONCURRENTLY and settled INDEPENDENTLY: a payments hiccup must never cost us the slug (no Preview
        // button), and a missing identity must never change the buy-box preview. A failure leaves it `null`
        // = unknown → the canvas falls back to its default rather than asserting a method (fail-open).
        const methodStatus = storeApi.getPaymentMethod().then(
          (v) => v,
          () => null,
        );
        try {
          const { slug, storeName, countryCode, currency, language, primaryDomain } =
            await storeApi.getIdentity();
          // The store's dictionary, BEFORE `storeLanguage` is published to the canvas. The order is the
          // whole point: `t()` answers from French until the chunk lands, so setting the language first
          // would paint an Arabic store in French for one round-trip and then snap. Free for a French
          // store (French is in the bundle) and one ~35 KB chunk for any other — against the 918 KB the
          // entry chunk carried when it held all twenty-six. Never rejects.
          await ensureStoreDictionary(language);
          const paymentMethod = await methodStatus;
          set((s) => {
            s.storeSlug = slug;
            s.storePrimaryDomain = primaryDomain ?? null;
            s.storeName = storeName;
            s.storeCountry = countryCode;
            s.storeLanguage = language;
            s.storeCurrency = currency;
            s.storePaymentMethod = paymentMethod;
            s.storeState = 'loaded';
          });
          // The identity may resolve AFTER the canvas built its context (they load concurrently), so
          // rebuild the render context with the real selling country → the cod-form's phone prefix flips
          // to the store's real dial code without a reload. Same for the card status: the buy-box
          // preview flips to the COD funnel the customer really gets when the store has no Stripe.
          const cur = get();
          if (cur.loadState === 'ready') {
            set({
              renderContext: buildEditContext(
                localeOf(cur.route, cur.storeLanguage),
                pageResourcesFor(cur.route, cur.previewProduct),
                cur.themeSettings,
                cur.codConfig,
                countryCode,
                currency,
                cur.previewOffer ?? undefined,
                paymentMethod ?? undefined,
              ),
            });
          }
        } catch {
          set((s) => {
            s.storeState = 'error';
          });
        }
      },

      async bindPreviewProduct(id) {
        // Bind by catalog id (feature 0021): null → the built-in demo; otherwise fetch the FULL product
        // (the render context needs the complete StorefrontProduct, not the lean list row) and swap it in.
        if (id == null) {
          set((s) => {
            s.previewProductTemplate = null;
            s.previewProductTemplateKnown = false;
          });
          get().setPreviewProduct(null);
          return;
        }
        const full = await catalogApi.getProduct(id);
        // Remember which template this product REALLY renders — the selector states it, and the navigation
        // below keeps it true.
        set((s) => {
          s.previewProductTemplate = full.templateSuffix ?? null;
          s.previewProductTemplateKnown = true;
        });
        get().setPreviewProduct(productResponseToStorefront(full));

        // FOLLOW THE PRODUCT. Choosing a product used to swap only the canvas data and leave the PREVIOUS
        // template open: the screen showed product B while the seller was still editing product A's layout,
        // and everything she typed went to a page product B does not render. She had no way to see it.
        // A product page IS its template, so picking the product opens the template that product renders.
        //
        // Only from the explicit picker (this action) — never from the cold-boot restore, where the URL owns
        // the route — and never inside a theme workspace, whose bound product is an ephemeral demo with no
        // catalog row and whose single draft is the theme's own page, not an assignable template.
        const route = get().route;
        const wanted = full.templateSuffix?.trim() || 'default';
        if (
          route.template === 'product' &&
          !isThemeWorkspaceHandle(route.handle) &&
          wanted !== route.handle
        ) {
          await get().load({ ...route, handle: wanted });
        }
      },

      updateThemeSettings(patch) {
        // Feature 0019 Slice 2. A theme edit is NOT a page-draft edit: it lives on the undo timeline and
        // re-themes the canvas live, but it must NOT dirty the page or bump its @Version, and it saves via
        // the DEDICATED versionless channel (scheduleThemeSave), never scheduleAutosave / serialize().
        // Consultation: a theme edit does not go through commit() (its own channel, see below), so it
        // needs its own gate — otherwise the colour pickers stayed live in a read-only tab.
        if (isReadOnlyView()) return;
        const st = get();
        const merged = mergeThemeSettings(st.themeSettings, patch);
        const context = buildEditContext(
          localeOf(st.route, st.storeLanguage),
          pageResourcesFor(st.route, st.previewProduct),
          merged,
          st.codConfig,
          st.storeCountry,
          st.storeCurrency,
          st.previewOffer ?? undefined,
          st.storePaymentMethod ?? undefined,
        );
        // Snapshot the PRE-edit state (incl. the OLD theme) onto history, coalescing a same-field burst
        // (a color/slider drag) into one undo entry — mirrors updateSectionSettings.
        const pre = snapshot(st);
        set((s) => {
          pushHistory(s, themeMergeKey(patch), pre);
          s.future = [];
        });
        // Object-set (NOT the Immer producer) for the readonly theme + function-bearing render context —
        // mirrors setPreviewProduct. Deliberately leaves `dirty` / `saveState` / `serverVersion` untouched.
        set({ themeSettings: merged, renderContext: context });
        // Inside a theme workspace the live channel is suppressed — persist the edit into THIS theme's
        // staged snapshot instead, so workspace color/typo tweaks survive refresh and re-apply.
        if (st.themeStagedForPublish && isThemeWorkspaceHandle(st.route.handle)) {
          persistStagedTheme(themeIdOfWorkspaceHandle(st.route.handle), merged);
        }
        scheduleThemeSave();
      },

      selectSection(sectionId) {
        // Selection alone is not an undoable edit (it travels WITH document snapshots instead), so it
        // does NOT go through commit() — selecting/deselecting must never create an undo entry or mark
        // the page dirty.
        set((s) => {
          s.selectedSectionId = sectionId;
          s.selectedBlockId = null;
          // One selection at a time: the Countdown app's surface is the editor's third selectable thing
          // (0041 V7), and two panels cannot be open at once.
          s.selectedCountdown = null;
          // A new node is selected → the previous part-drill no longer applies. `focusField` (if any)
          // runs AFTER this, so a drill-in sets the section then re-focuses the field in one gesture.
          s.focusedFieldId = null;
        });
        /*
          Clicking the reviews band is the RETRY — the one the failure message points at.

          A failed fetch is not cached, so this asks again; a shop that genuinely has no published review
          IS cached and this costs nothing. Without it the only way out of a failed preview was reloading
          the whole editor, which is not something the band ever told anyone to do.
        */
        if (sectionId && get().sectionsById[sectionId]?.type === 'product-reviews') {
          void refreshPreviewReviews(get().previewProduct, { retryEmpty: true });
        }
      },

      selectBlock(blockId) {
        set((s) => {
          s.selectedBlockId = blockId;
          s.focusedFieldId = null;
        });
      },

      focusField(fieldId) {
        // UI-only (like `selectMode`): frames the element in the canvas + scrolls the inspector to that
        // field. Never undoable, never dirtying. The caller selects the owning node FIRST, then focuses.
        set((s) => {
          s.focusedFieldId = fieldId;
        });
      },

      // ── UI-only view actions (M3.5) — never go through commit() (not undoable, not dirtying) ──
      setDevice(device) {
        persistDevice(device);
        set((s) => {
          s.device = device;
        });
      },

      setPreviewMode(on) {
        set((s) => {
          s.previewMode = on;
          if (on) {
            s.selectMode = false; // clean preview hides the chrome → select mode is moot there
            s.focusedFieldId = null; // …and no part-frame should show over the clean preview
          }
        });
      },

      togglePreviewMode() {
        set((s) => {
          s.previewMode = !s.previewMode;
          if (s.previewMode) {
            s.selectMode = false;
            s.focusedFieldId = null;
          }
        });
      },

      setSelectMode(on) {
        set((s) => {
          s.selectMode = on;
        });
      },

      toggleSelectMode() {
        set((s) => {
          s.selectMode = !s.selectMode;
        });
      },

      setPreviewDir(dir) {
        // Flip the PREVIEW (canvas content) direction only — never the builder chrome. Mutating
        // `renderContext.dir` re-themes the `[data-aqx-page]` wrapper AND re-feeds every rendered section
        // the new dir. UI-only: not committed, so it never dirties the document or pushes an undo entry.
        set((s) => {
          if (s.renderContext) s.renderContext.dir = dir;
        });
      },

      togglePreviewDir() {
        set((s) => {
          if (s.renderContext) s.renderContext.dir = s.renderContext.dir === 'rtl' ? 'ltr' : 'rtl';
        });
      },

      requestCanvasReveal() {
        // UI-only (like `selectMode`/`focusField`): a monotonic bump the canvas keys a scroll effect on.
        // Caller selects the node FIRST, then requests the reveal, so the effect scrolls the settled
        // selection. Never dirties/undoes — selecting a component must not create an edit.
        set((s) => {
          s.revealNonce += 1;
        });
      },

      // ── section structural reducers ─────────────────────────────────────────
      insertSection(type, atIndex, presetIndex = 0) {
        const def = sectionRegistry[type];
        if (!def) {
          pushToast(btp('chrome.sec.unknown', 'Unknown section type "{type}".', { type }), 'error');
          return;
        }
        commit((s) => {
          // The shop's own language (lot 0), so a section added to an Arabic shop starts in Arabic
          // rather than saving French into the seller's document for them to retype.
          const inst = instantiateSection(def, presetIndex, get().storeLanguage || undefined);
          const at =
            atIndex == null ? s.order.length : Math.max(0, Math.min(atIndex, s.order.length));
          s.order.splice(at, 0, inst.id);
          s.sectionsById[inst.id] = inst;
          // Select the freshly inserted section so the inspector opens on it, and scroll it into view —
          // adding a section while scrolled elsewhere should jump the preview to the new section (Shopify).
          // `revealNonce` isn't part of the history Snapshot, so bumping it here is undo-safe.
          s.selectedSectionId = inst.id;
          s.selectedBlockId = null;
          s.revealNonce += 1;
        });
        /*
          ADDING the reviews band is a moment to ask again (founder, 18/09/2026: "il doit montrer un
          spinner s'il a besoin de les charger et après m'afficher les avis").

          Until here, the reviews were asked for exactly twice — at boot and on a product bind — so a
          section dropped onto the page later simply painted whatever the boot had concluded. When the
          boot call had FAILED (a backend that went down, a session that ended), that conclusion was
          «0 avis», and inserting the section showed it instantly, with no spinner and no way back.
          No-op on a cached answer and on a call already in flight, so a page full of insertions costs
          at most the one request that was missing.
        */
        if (type === 'product-reviews') {
          void refreshPreviewReviews(get().previewProduct, { retryEmpty: true });
        }
      },

      moveSection(id, toIndex) {
        const { order, sectionsById } = get();
        const from = order.indexOf(id);
        if (from === -1) return;
        // A section never leaves its rail group (HEADER · TEMPLATE · FOOTER). The rail already enforced
        // this with one DndContext per group; the canvas didn't, so a drop at the very top slid a body
        // section ABOVE the header — silently when the header was hidden (zero-height, nothing to see).
        const to = clampToZone(order, sectionsById, id, toIndex);
        if (to === from) return;
        commit((s) => {
          reorderInPlace(s.order, from, to);
        });
      },

      removeSection(id) {
        if (!get().sectionsById[id]) return;
        commit((s) => {
          const idx = s.order.indexOf(id);
          if (idx !== -1) s.order.splice(idx, 1);
          delete s.sectionsById[id];
          // Clear selection if the removed section (or its block) was selected.
          if (s.selectedSectionId === id) {
            s.selectedSectionId = null;
            s.selectedBlockId = null;
          }
        });
      },

      duplicateSection(id) {
        const src = get().sectionsById[id];
        if (!src) return;
        // Deep-copy (structuredClone) so the clone shares NO settings/blocks refs with the source, then mint
        // fresh ids for the section + its whole block tree (reidBlocks). Insert right AFTER the original and
        // select it. This IS an edit (undoable via commit), unlike a preview-product bind.
        const cloned = structuredClone(src);
        const clone: SectionInstance = {
          ...cloned,
          id: nanoid(),
          blocks: reidBlocks(cloned.blocks) ?? [],
        };
        commit((s) => {
          const idx = s.order.indexOf(id);
          if (idx === -1) return;
          s.order.splice(idx + 1, 0, clone.id);
          s.sectionsById[clone.id] = clone;
          s.selectedSectionId = clone.id;
          s.selectedBlockId = null;
          s.revealNonce += 1; // scroll the new copy into view (undo-safe: not in the Snapshot)
        });
      },

      toggleSectionDisabled(id) {
        if (!get().sectionsById[id]) return;
        commit((s) => {
          const section = s.sectionsById[id];
          if (!section) return;
          section.disabled = !section.disabled;
        });
      },

      // ── block structural reducers (recursive Shrine tree) ────────────────────
      addBlock(sectionId, blockType, atIndex, parentBlockId) {
        const section = get().sectionsById[sectionId];
        if (!section) return;
        const def = sectionRegistry[section.type];
        if (!def) return;
        // Resolve the TARGET container: the section (parentBlockId null) or a nested block.
        const parent = parentBlockId ? (findBlockCtx(section, parentBlockId)?.block ?? null) : null;
        if (parentBlockId && !parent) return;
        // Validate against the container's accepts list (the Shrine drop-validation matrix).
        if (!acceptedTypes(def, parent).includes(blockType)) {
          pushToast(
            btp('chrome.sec.block_rejected', 'This can’t take a "{type}" block.', {
              type: blockType,
            }),
            'error',
          );
          return;
        }
        const bschema = blockSchemaByType(def, blockType);
        if (!bschema) {
          pushToast(
            btp('chrome.sec.block_unknown', 'Unknown block type "{type}".', { type: blockType }),
            'error',
          );
          return;
        }
        // Per-type cap within the target container; section-wide cap counts the WHOLE tree.
        const targetBlocks = parent ? (parent.blocks ?? []) : section.blocks;
        const ofType = targetBlocks.filter((b) => b.type === blockType).length;
        if (bschema.max != null && ofType >= bschema.max) {
          pushToast(
            btp('chrome.sec.block_max', 'You can add at most {max} {name} blocks.', {
              max: bschema.max,
              name: bschema.name,
            }),
            'error',
          );
          return;
        }
        const maxBlocks = def.schema.maxBlocks;
        if (maxBlocks != null && countBlocksDeep(section.blocks) >= maxBlocks) {
          pushToast(
            btp('chrome.sec.section_max', 'This section is limited to {max} blocks.', {
              max: maxBlocks,
            }),
            'error',
          );
          return;
        }
        commit((s) => {
          const sec = s.sectionsById[sectionId];
          if (!sec) return;
          const block = instantiateBlock(def, blockType);
          let arr: Block[] = sec.blocks;
          if (parentBlockId) {
            const ctx = findBlockCtx(sec, parentBlockId);
            if (!ctx) return;
            if (!ctx.block.blocks) ctx.block.blocks = [];
            arr = ctx.block.blocks;
          }
          const at = atIndex == null ? arr.length : Math.max(0, Math.min(atIndex, arr.length));
          arr.splice(at, 0, block);
          // Keep the section selected; open the new block in the inspector.
          s.selectedSectionId = sectionId;
          s.selectedBlockId = block.id;
        });
      },

      moveBlock(sectionId, blockId, toIndex) {
        const section = get().sectionsById[sectionId];
        if (!section) return;
        const ctx = findBlockCtx(section, blockId);
        // Static children (Gallery/Details) are theme-fixed — never reorderable.
        if (!ctx || ctx.block.static) return;
        commit((s) => {
          const sec = s.sectionsById[sectionId];
          if (!sec) return;
          const d = findBlockCtx(sec, blockId);
          if (!d) return;
          const from = d.siblings.findIndex((b) => b.id === blockId);
          reorderInPlace(d.siblings, from, toIndex);
        });
      },

      removeBlock(sectionId, blockId) {
        const section = get().sectionsById[sectionId];
        if (!section) return;
        const ctx = findBlockCtx(section, blockId);
        // Static children can't be deleted.
        if (!ctx || ctx.block.static) return;
        commit((s) => {
          const sec = s.sectionsById[sectionId];
          if (!sec) return;
          const d = findBlockCtx(sec, blockId);
          if (!d) return;
          const idx = d.siblings.findIndex((b) => b.id === blockId);
          if (idx !== -1) d.siblings.splice(idx, 1);
          if (s.selectedBlockId === blockId) s.selectedBlockId = null;
        });
      },

      duplicateBlock(sectionId, blockId) {
        const section = get().sectionsById[sectionId];
        if (!section) return;
        const ctx = findBlockCtx(section, blockId);
        if (!ctx || ctx.block.static) return; // static children are part of a fixed structure — not duplicable
        // Respect the same caps as addBlock: section-wide maxBlocks + the per-type cap within the container.
        const def = sectionRegistry[section.type];
        const addCount = countBlocksDeep([ctx.block]);
        const maxBlocks = def?.schema.maxBlocks;
        if (maxBlocks != null && countBlocksDeep(section.blocks) + addCount > maxBlocks) {
          pushToast(
            btp('chrome.sec.section_max', 'This section is limited to {max} blocks.', {
              max: maxBlocks,
            }),
            'error',
          );
          return;
        }
        const bschema = def ? blockSchemaByType(def, ctx.block.type) : undefined;
        const ofType = ctx.siblings.filter((b) => b.type === ctx.block.type).length;
        if (bschema?.max != null && ofType >= bschema.max) {
          pushToast(
            btp('chrome.sec.block_max', 'You can add at most {max} {name} blocks.', {
              max: bschema.max,
              name: bschema.name,
            }),
            'error',
          );
          return;
        }
        // Deep-copy + fresh ids for the block subtree, then insert right AFTER the original among its siblings.
        const cloned = structuredClone(ctx.block);
        const clone: Block = { ...cloned, id: nanoid(), blocks: reidBlocks(cloned.blocks) };
        commit((s) => {
          const sec = s.sectionsById[sectionId];
          if (!sec) return;
          const d = findBlockCtx(sec, blockId);
          if (!d) return;
          const idx = d.siblings.findIndex((b) => b.id === blockId);
          if (idx === -1) return;
          d.siblings.splice(idx + 1, 0, clone);
          s.selectedSectionId = sectionId;
          s.selectedBlockId = clone.id;
        });
      },

      toggleBlockDisabled(sectionId, blockId) {
        const section = get().sectionsById[sectionId];
        if (!section || !findBlockCtx(section, blockId)) return;
        commit((s) => {
          const sec = s.sectionsById[sectionId];
          const d = sec ? findBlockCtx(sec, blockId) : null;
          if (!d) return;
          d.block.disabled = !d.block.disabled;
        });
      },

      // ── settings reducers (now history-wrapped + coalesced) ──────────────────
      updateSectionSettings(sectionId, patch) {
        if (!get().sectionsById[sectionId]) return;
        // Coalesce rapid same-target same-field edits (a slider drag / consecutive keystrokes) into ONE
        // undo entry within COALESCE_MS (build-plan §5.3).
        const mergeKey = `secset:${sectionId}:${Object.keys(patch).sort().join(',')}`;
        commit((s) => {
          const section = s.sectionsById[sectionId];
          if (!section) return;
          Object.assign(section.settings, patch);
        }, mergeKey);
      },

      updateBlockSettings(sectionId, blockId, patch) {
        const section = get().sectionsById[sectionId];
        if (!section || !findBlockCtx(section, blockId)) return;
        const mergeKey = `blkset:${sectionId}:${blockId}:${Object.keys(patch).sort().join(',')}`;
        commit((s) => {
          const sec = s.sectionsById[sectionId];
          const d = sec ? findBlockCtx(sec, blockId) : null;
          if (!d) return;
          Object.assign(d.block.settings, patch);
        }, mergeKey);
      },

      applyGeneratedText(patches) {
        // Validate against the COMPOSED surface (chrome included — a recipe may brief the header one
        // day), through the same function the generation plan was built from. applyTextSlots deep-clones
        // and never throws: a model-invented address or an over-long value is a reject, not a write.
        const before = serializeComposed(get());
        const result = applyTextSlots(before, patches, sectionRegistry);
        if (result.applied > 0) {
          const changed = new Set(patches.map((p) => p.path[0]));
          const nextById = new Map(result.document.sections.map((s) => [s.id, s]));
          // NO mergeKey: one generated section = one undo frame. Coalescing two sections into one
          // frame would make Ctrl-Z erase more than the seller saw being written.
          commit((s) => {
            for (const id of changed) {
              const next = id ? nextById.get(id) : undefined;
              const target = id ? s.sectionsById[id] : undefined;
              if (!next || !target) continue;
              target.settings = next.settings;
              target.blocks = next.blocks;
            }
          });
        }
        return { applied: result.applied, rejected: result.rejected.length };
      },

      // ── undo/redo (snapshot stacks; restore document, theme, AND selection exactly) ──
      // A frame may carry a document change, a theme change, or both (feature 0019). Each is persisted
      // through its OWN channel: a document change dirties the page + schedules the page autosave; a theme
      // change rebuilds the render context + schedules the versionless theme save. A theme-only undo does
      // NOT dirty the page (sameDocument guards it), so it never fires a spurious page PUT.
      undo() {
        const st0 = get();
        const prev = st0.past[st0.past.length - 1];
        if (!prev) return;
        const docChanged = !sameDocument(st0, prev);
        const themeChanged = st0.themeSettings !== prev.themeSettings;
        set((s) => {
          s.past.pop();
          s.future.push(snapshot(s));
          applySnapshot(s, prev);
          // A part-drill is UI-only (not snapshotted); drop it so no stale frame survives the restore.
          s.focusedFieldId = null;
          // Undo breaks any coalescing run so the NEXT edit always starts a fresh frame.
          s._lastMergeKey = null;
          s._lastEditAt = 0;
          if (docChanged) {
            s.dirty = true;
            s.saveState = 'unsaved';
          }
        });
        if (themeChanged) applyRestoredTheme();
        if (docChanged) scheduleAutosave();
      },

      redo() {
        const st0 = get();
        const next = st0.future[st0.future.length - 1];
        if (!next) return;
        const docChanged = !sameDocument(st0, next);
        const themeChanged = st0.themeSettings !== next.themeSettings;
        set((s) => {
          s.future.pop();
          s.past.push(snapshot(s));
          applySnapshot(s, next);
          s.focusedFieldId = null;
          s._lastMergeKey = null;
          s._lastEditAt = 0;
          if (docChanged) {
            s.dirty = true;
            s.saveState = 'unsaved';
          }
        });
        if (themeChanged) applyRestoredTheme();
        if (docChanged) scheduleAutosave();
      },

      canUndo() {
        return get().past.length > 0;
      },
      canRedo() {
        return get().future.length > 0;
      },
    };
  }),
);
